/**
 * The Team page's Procore layer — Led AI INTERNAL.
 *
 * For a Led AI staff member who is an admin of a customer's workspace,
 * looking at its Team page, this adds what
 * the pilot's people record in PROCORE beside what they do in Led AI: an
 * "In Procore" card above the roster (the weekly trend against the pilot's
 * own baseline, by record kind, narrowable to items with an assignee /
 * location / description), a trend + change on every roster row in the same
 * lens, and a row for each chosen person who is not on Led AI yet. Customers,
 * and workspace members who are not admins, never see any of it — the
 * backend's gate (staff, then admin of the org) answers 403 and the Team
 * page renders as it always has.
 *
 * ## One setup, then it fills itself in
 *
 * Until a staff member sets the workspace up, the card is an empty chart
 * with one prompt. Setup is three steps in one sheet: when the pilot
 * started; a confirmation that Led AI will read the customer's Procore once
 * (every call lands on THEIR account, under THEIR users' connections); and
 * who is in the pilot, picked from the Procore people the read found. After
 * that nothing is asked again: the backend re-reads Procore once a week
 * until the pilot ends (the step-2 consent names that cadence), and the Led
 * AI side is the Team roster, which fills in on its own as each chosen
 * person onboards —
 * a chosen person with no seat yet is a roster row with empty Led AI
 * columns, and becomes an ordinary member row the day they sign in.
 *
 * `People.tsx` owns the page; this file owns everything Procore on it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  deleteMutation,
  fetchInvite,
  fetchProcoreBaseline,
  fetchProcoreBaselineDirectory,
  type ProcoreDirectoryEnvelope,
  postMutation,
  saveProcoreBaselineSetup,
  startProcoreBaselinePull,
  type InviteResponse,
  type OrgInvitationRow,
  type ProcoreBaselineEnvelope,
} from "../api";
import type {
  ProcoreBaselineCohortMember,
  ProcoreBaselineCounts,
  ProcoreBaselinePull,
  ProcoreBaselineResponse,
  ProcoreBaselineSchedule,
  ProcoreBaselineSetup,
  ProcoreBaselineProject,
  ProcoreBaselineUserRow,
  ProcoreBaselineWeek,
  ProcoreBaselineWindow,
} from "../../../../backend/src/shared/procoreBaseline";
import {
  DEFAULT_SCHEDULE,
  addDays,
  EMPTY_BASELINE_COUNTS,
  SCHEDULE_LIMITS,
  addCounts,
  aggregateUsersAcrossProjects,
  authoredTotal,
  buildProcoreBaselineWindows,
  nextWeeklyReadAt,
  percentChange,
  pilotReadsEndOn,
  scheduleProblem,
  seriesAt,
  sumByDay,
  weeklyRate,
  daysBetween,
  rampWindow,
  pilotCalendar,
  rampMakeup,
  weeklySeries,
  DIRECTORY_PAGE_DEFAULT,
  type ProcoreBaselineGrain,
  type ProcoreDirectoryRow,
} from "../../../../backend/src/shared/procoreBaseline";
import { Sparkline, Trend, type SeriesValue } from "../components/WeeklySparkline";
import { useResource } from "../useResource";
import { C, DS, FONT_MONO } from "../tokens";
import { plural, whenStamp } from "../components/chrome";

/** The Team page's fetch gate; the backend's allowlist is the law. */
export function looksLikeLedStaff(email: string | null | undefined): boolean {
  return !!email && email.trim().toLowerCase().endsWith("@ledai.build");
}

/** Whether the Team page offers invites: the Invite button in its header, and
 *  Send invite on a pilot person who is not on Led AI yet. Off until the
 *  invite flow is defined: today it sends Clerk's generic
 *  join-the-organization email with no word about the app, and a resend can
 *  fail with a bare 502. Off, the row says "Not yet joined" and the page
 *  does not read the invitations at all. An object so the tests can turn it on. */
export const TEAM_INVITES = { enabled: false };

/** How often the page re-reads the STORED state while a read runs. An
 *  object so the test suite can shorten it; never a Procore call. */
export const PROCORE_POLL = { ms: 3_000 };
/** The page's clock. An object so the test suite can pin a date; the pilot's
 *  calendar is whole weeks, so what "today" is decides what has ended. */
export const CLOCK = { now: () => new Date() };
/** How long the people picker waits after a keystroke before asking the backend. */
export const DIRECTORY_DEBOUNCE_MS = 150;

export type ProcoreMetric = "authored" | "dailyLogs" | "observations" | "tasks" | "punchCreated" | "photos";

export const PROCORE_METRICS: Array<{ id: ProcoreMetric; label: string; unit: string; hint: string }> = [
  { id: "authored", label: "All", unit: "records", hint: "Daily logs + observations + tasks + punch created." },
  { id: "dailyLogs", label: "Daily log entries", unit: "daily log entries", hint: "Daily-log entries, by log date." },
  { id: "observations", label: "Observations", unit: "observations", hint: "Observations created." },
  { id: "tasks", label: "Tasks", unit: "tasks", hint: "Tasks created." },
  { id: "punchCreated", label: "Punch", unit: "punch items", hint: "Punch items created." },
  { id: "photos", label: "Photos", unit: "photos", hint: "Photos uploaded." },
];

/**
 * What the card and every roster cell count: a tab, and the filters the tab
 * offers — observations and punch items can be narrowed to those with an
 * assignee and/or a location, photos to those with a description and/or a
 * location. One lens for the whole page, so the hero, the trend and each
 * person's sparkline always agree.
 */
export interface ProcoreLens {
  metric: ProcoreMetric;
  assignee: boolean;
  location: boolean;
  description: boolean;
}
export const DEFAULT_LENS: ProcoreLens = Object.freeze({ metric: "authored", assignee: false, location: false, description: false });

export type ProcoreFilterKey = "assignee" | "location" | "description";

/** The filters a tab offers, in the artboard's order. None for All and Daily log entries; tasks take the observations' pair. */
export function lensFilters(metric: ProcoreMetric): Array<{ key: ProcoreFilterKey; label: string; icon: "person" | "pin" | "list" }> {
  if (metric === "observations" || metric === "punchCreated" || metric === "tasks") {
    return [
      { key: "assignee", label: "Has assignee", icon: "person" },
      { key: "location", label: "Has location", icon: "pin" },
    ];
  }
  if (metric === "photos") {
    return [
      { key: "description", label: "Has description", icon: "list" },
      { key: "location", label: "Has location", icon: "pin" },
    ];
  }
  return [];
}

/** The filters that are on AND apply to the tab (a switch left on under
 *  another tab is remembered, not applied). */
function activeFilters(lens: ProcoreLens): ProcoreFilterKey[] {
  return lensFilters(lens.metric).map((f) => f.key).filter((k) => lens[k]);
}

/** The counter a lens reads. Two filters on → the intersection counter. */
export function lensValue(counts: ProcoreBaselineCounts | undefined, lens: ProcoreLens): number {
  if (!counts) return 0;
  const on = activeFilters(lens);
  const pick = (f: keyof ProcoreBaselineCounts) => counts[f] ?? 0;
  switch (lens.metric) {
    case "authored":
      return authoredTotal(counts);
    case "dailyLogs":
      return pick("dailyLogs");
    case "observations":
      return on.length === 2 ? pick("observationsWithBoth") : on[0] === "assignee" ? pick("observationsWithAssignee") : on[0] === "location" ? pick("observationsWithLocation") : pick("observations");
    case "punchCreated":
      return on.length === 2 ? pick("punchWithBoth") : on[0] === "assignee" ? pick("punchWithAssignee") : on[0] === "location" ? pick("punchWithLocation") : pick("punchCreated");
    case "tasks":
      return on.length === 2 ? pick("tasksWithBoth") : on[0] === "assignee" ? pick("tasksWithAssignee") : on[0] === "location" ? pick("tasksWithLocation") : pick("tasks");
    case "photos":
      return on.length === 2 ? pick("photosWithBoth") : on[0] === "description" ? pick("photosWithDescription") : on[0] === "location" ? pick("photosWithLocation") : pick("photos");
  }
}

/** "observations with an assignee and a location" — the unit the hero
 *  sentence and the footer read. */
export function lensUnit(lens: ProcoreLens): string {
  const base = PROCORE_METRICS.find((m) => m.id === lens.metric)!.unit;
  const words: Record<ProcoreFilterKey, string> = { assignee: "an assignee", location: "a location", description: "a description" };
  const on = activeFilters(lens).map((k) => words[k]);
  return on.length === 0 ? base : `${base} with ${on.join(" and ")}`;
}

/** "daily log entries with a location" → "daily log entry with a location". */
export function singular(unit: string): string {
  return unit.replace(/ies( with|$)/, "y$1").replace(/s( with|$)/, "$1");
}

/** Kept for the roster's older call sites: a bare tab, no filters. */
export function metricValue(counts: ProcoreBaselineCounts | undefined, metric: ProcoreMetric): number {
  return lensValue(counts, { ...DEFAULT_LENS, metric });
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

/**
 * The stored read + setup for this workspace, fetched only when `enabled`.
 * A 403 or a missing route leaves `response` null and the Team page
 * unchanged — the layer is additive, never a failure the customer's page
 * has to render.
 */
export function useProcoreBaseline(search: string, enabled: boolean, refreshKey: number) {
  const [tick, setTick] = useState(0);
  const { data, failure } = useResource<ProcoreBaselineEnvelope>(
    enabled ? `procore-baseline:${search}` : null,
    () => fetchProcoreBaseline(search),
    refreshKey + tick,
  );
  const response: ProcoreBaselineResponse | null = data?.result ?? null;
  const pull = response?.pull ?? null;
  const active = pull?.status === "queued" || pull?.status === "running";

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((t) => t + 1), PROCORE_POLL.ms);
    return () => clearInterval(id);
  }, [active]);

  // Settled once the first answer is in — the response or a refusal — so the
  // page can wait for it and paint once. A poll's refresh keeps `data`, so a
  // running read never un-settles it.
  return { response, denied: !!failure, settled: !enabled || data !== null || failure !== null, refresh: () => setTick((t) => t + 1) };
}

/**
 * The organization's pending invitations, for the pilot's people who are not
 * on Led AI yet: whether each has been invited, and whether the caller may
 * invite. Read beside the Procore layer, not after it, so the page can wait
 * for both and paint once. The same console route the Invite page reads; a
 * refusal leaves `pending` empty and the buttons disabled with the reason.
 */
export function useInvitations(search: string, enabled: boolean, refreshKey: number) {
  const [tick, setTick] = useState(0);
  const { data, failure } = useResource<InviteResponse>(
    enabled ? `invite:${search}` : null,
    () => fetchInvite(search),
    refreshKey + tick,
  );
  const byEmail = new Map<string, OrgInvitationRow>();
  for (const row of data?.pending ?? []) byEmail.set(row.email.toLowerCase(), row);
  return {
    byEmail,
    canInvite: data?.canInvite ?? false,
    reason: failure ? "Could not load invitations." : data && !data.canInvite ? data.readOnlyReason ?? "You cannot invite here." : null,
    settled: !enabled || data !== null || failure !== null,
    refresh: () => setTick((t) => t + 1),
  };
}

export interface InviteContext {
  search: string;
  canInvite: boolean;
  reason: string | null;
  /** Pending invitation by lower-cased email. */
  byEmail: Map<string, OrgInvitationRow>;
  /** Re-read the invitations after one was sent. */
  onChanged: () => void;
}

/** A chosen person, resolved against the read. */
export interface CohortPerson {
  member: ProcoreBaselineCohortMember;
  /** The read's row for them — undefined when the read found no activity. */
  row: ProcoreBaselineUserRow | undefined;
  /** Their Led AI seat, when the read matched one by email. */
  ledUserId: string | undefined;
}

/**
 * What the charts bin over. The WINDOWS are the workspace's — its pilot date
 * and checkpoint schedule, evaluated today — so editing the schedule redraws
 * the page at once from the stored per-day counts; the SPAN is the read's,
 * because that is the stretch of days there is data for.
 */
export interface ProcoreFrame {
  windows: ProcoreBaselineWindow[];
  span: { start: string; end: string };
  pilotStartDate?: string;
  /** The pilot's last day, from the schedule — the chart spans to it. */
  horizon?: string;
  /** The day the windows were evaluated for; rates count through the day before. */
  today: string;
}

/** Everything the roster needs per seat, plus the chosen people without one. */
export interface ProcoreIndex {
  pull: ProcoreBaselinePull;
  setup: ProcoreBaselineSetup | null;
  frame: ProcoreFrame;
  hasPilot: boolean;
  /** Clerk user id → that person's Procore row (summed across projects). */
  bySeat: Map<string, ProcoreBaselineUserRow>;
  /** Clerk user ids that are in the pilot. Empty until people are chosen. */
  pilotSeats: Set<string>;
  /** Chosen people with no Led AI seat yet — they get their own roster rows. */
  pending: CohortPerson[];
  /** The trend's pool: the chosen people, or every matched seat before
   *  anyone has been chosen. */
  pool: ProcoreBaselineUserRow[];
}

export function indexPull(pull: ProcoreBaselinePull | null, setup: ProcoreBaselineSetup | null, today: string = CLOCK.now().toISOString().slice(0, 10)): ProcoreIndex | null {
  if (!pull || pull.projects.length === 0) return null;
  const users = aggregateUsersAcrossProjects(pull);
  const pilotStartDate = setup?.pilotStartDate ?? pull.pilotStartDate;
  const frame: ProcoreFrame = pilotStartDate
    ? {
        windows: buildProcoreBaselineWindows({ pilotStartDate, today, schedule: setup?.schedule ?? null }).windows,
        span: pull.span,
        pilotStartDate,
        horizon: pilotCalendar(pilotStartDate, setup?.schedule ?? null).pilotEnd,
        today,
      }
    : { windows: pull.windows, span: pull.span, pilotStartDate: undefined, today };
  const byProcoreId = new Map(users.map((u) => [u.procoreUserId, u]));
  const bySeat = new Map<string, ProcoreBaselineUserRow>();
  for (const u of users) if (u.ledUserId) bySeat.set(u.ledUserId, u);

  const cohort = setup?.cohortChosen ? setup.cohort : [];
  const pilotSeats = new Set<string>();
  const pending: CohortPerson[] = [];
  const pool: ProcoreBaselineUserRow[] = [];
  for (const member of cohort) {
    const row = byProcoreId.get(member.procoreUserId);
    const ledUserId = row?.ledUserId;
    if (ledUserId) pilotSeats.add(ledUserId);
    else pending.push({ member, row, ledUserId: undefined });
    if (row) pool.push(row);
  }
  return {
    pull,
    setup,
    frame,
    hasPilot: !!pilotStartDate,
    bySeat,
    pilotSeats,
    pending,
    pool: cohort.length > 0 ? pool : [...bySeat.values()],
  };
}

// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------

/** A window's weekly pace for a group of people — see `weeklyRate`. Null
 *  until the window has one complete day. */
function poolRate(byDay: Record<string, ProcoreBaselineCounts> | undefined, frame: ProcoreFrame, key: string, value: SeriesValue): number | null {
  return weeklyRate(byDay, frame.windows.find((w) => w.key === key), frame.today, value);
}

/** Has the window run its course? Today is never complete, so a window
 *  that ends today is still running. */
export const hasEnded = (w: ProcoreBaselineWindow, today: string) => !w.future && !w.partial && w.end < today;

/** The period the hero compares against the baseline: the latest checkpoint
 *  that has ENDED, or — until CP1 ends — the ramp-up once it has. A period
 *  still under way shows its pace in the footer without a change figure:
 *  only finished periods are compared (Team Web artboard, 2026-10-08). */
export function endedPhase(windows: ProcoreBaselineWindow[], today: string): ProcoreBaselineWindow | undefined {
  const cps = windows.filter((w) => /^cp\d$/.test(w.key) && hasEnded(w, today));
  if (cps.length) return cps[cps.length - 1];
  const ramp = rampWindow(windows, today);
  return ramp && hasEnded(ramp, today) ? ramp : undefined;
}

/** The period under way today: the latest begun checkpoint, else the ramp-up. */
export function runningPhase(windows: ProcoreBaselineWindow[], today: string): ProcoreBaselineWindow | undefined {
  const cps = windows.filter((w) => /^cp\d$/.test(w.key) && !w.future && !hasEnded(w, today));
  if (cps.length) return cps[cps.length - 1];
  const ramp = rampWindow(windows, today);
  return ramp && !ramp.future && !hasEnded(ramp, today) ? ramp : undefined;
}

/** "CP2", or "the ramp-up" — how a period is named in a sentence. */
export const phaseName = (w: ProcoreBaselineWindow) => (w.key === "ramp" ? "the ramp-up" : cpName(w));

export const cpName = (w: ProcoreBaselineWindow) => w.label.split(" · ")[0]!;

export function fmtAvg(v: number | null): string {
  if (v === null) return "—";
  return v >= 10 ? String(Math.round(v)) : String(Math.round(v * 10) / 10);
}
export function fmtDelta(change: number | null): string {
  return change === null ? "—" : `${change > 0 ? "+" : ""}${change}%`;
}

/** A person's counts inside one window, from their per-day ticks. */
export function sumInWindow(byDay: Record<string, ProcoreBaselineCounts> | undefined, w: ProcoreBaselineWindow | undefined): ProcoreBaselineCounts {
  const out = { ...EMPTY_BASELINE_COUNTS };
  if (!byDay || !w || w.future) return out;
  for (const [ymd, c] of Object.entries(byDay)) if (ymd >= w.start && ymd <= w.end) addCounts(out, c);
  return out;
}

export function personFigures(index: ProcoreIndex, row: ProcoreBaselineUserRow | undefined, lens: ProcoreLens) {
  const value: SeriesValue = (c) => lensValue(c, lens);
  const weeks = weeklySeries(index.frame, row?.byDay);
  const phase = index.hasPilot ? endedPhase(index.frame.windows, index.frame.today) : undefined;
  const drawn = weeks.filter((w) => !w.future);
  const before = index.hasPilot ? poolRate(row?.byDay, index.frame, "baseline", value) : drawn.length > 1 ? value(drawn[drawn.length - 2]!.counts) : null;
  const now = index.hasPilot ? (phase ? weeklyRate(row?.byDay, phase, index.frame.today, value) : null) : drawn.length > 0 ? value(drawn[drawn.length - 1]!.counts) : null;
  const change = before !== null && now !== null ? percentChange(before, now) : null;
  return { weeks, before, now, change, value };
}

// ---------------------------------------------------------------------------
// Pieces, in the Team artboard's own ink
// ---------------------------------------------------------------------------

const LABEL: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  color: DS.meta,
};
const MUTED: React.CSSProperties = { fontSize: 14, lineHeight: 1.5, color: DS.body2 };

/** The design system's Button: ink pill by default (`primary`), white with a
 *  13% hairline (`secondary`), bare (`ghost`), gold (`accent`). 36px `sm`, 44px `md`. */
export function DsButton({ children, variant = "primary", size = "sm", disabled, busy, onClick, testId, leftIcon, style }: { children: React.ReactNode; variant?: "primary" | "secondary" | "ghost" | "accent"; size?: "sm" | "md"; disabled?: boolean; busy?: boolean; onClick?: () => void; testId?: string; leftIcon?: React.ReactNode; style?: React.CSSProperties }) {
  const off = disabled || busy;
  const look: React.CSSProperties =
    variant === "primary"
      ? { background: DS.ink, color: DS.surface, border: "none", boxShadow: "0 4px 12px rgba(10,10,10,.14)" }
      : variant === "accent"
        ? { background: DS.gold, color: DS.ink, border: "none", boxShadow: DS.shadowCard }
        : variant === "secondary"
          ? { background: DS.surface, color: DS.ink, border: `1px solid ${DS.line2}`, boxShadow: DS.shadowCard }
          : { background: "transparent", color: DS.link, border: "none", boxShadow: "none" };
  return (
    <button
      className="kb"
      data-testid={testId}
      disabled={off}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: size === "sm" ? 7 : 8,
        height: size === "sm" ? 36 : 44,
        padding: size === "sm" ? "0 14px" : "0 18px",
        borderRadius: 999,
        fontFamily: "inherit",
        fontSize: size === "sm" ? 13.5 : 14,
        fontWeight: 700,
        letterSpacing: "-0.1px",
        lineHeight: 1,
        whiteSpace: "nowrap",
        cursor: off ? (busy ? "wait" : "not-allowed") : "pointer",
        opacity: off ? 0.45 : 1,
        transition: "transform 140ms cubic-bezier(0.2,0.8,0.2,1), opacity 140ms",
        ...look,
        ...style,
      }}
      onMouseDown={(e) => { if (!off) e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
    >
      {leftIcon}
      {children}
    </button>
  );
}

/** The design system's Pill: mono, uppercase, soft fill in its tone. */
export function DsPill({ children, tone = "neutral", title, dot = false, testId }: { children: React.ReactNode; tone?: "neutral" | "muted" | "gold" | "live" | "warning" | "danger"; title?: string; dot?: boolean; testId?: string }) {
  const t = {
    neutral: { c: DS.ink, soft: DS.surfaceSunk },
    muted: { c: DS.meta, soft: DS.surfaceSunk },
    gold: { c: DS.goldInk, soft: DS.gold },
    live: { c: DS.green, soft: DS.greenSoft },
    warning: { c: DS.amber, soft: DS.amberSoft },
    danger: { c: DS.red, soft: DS.redSoft },
  }[tone];
  return (
    <span title={title} data-testid={testId} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: FONT_MONO, fontSize: 9, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", color: t.c, background: t.soft, border: "1px solid transparent", padding: "3px 7px", borderRadius: 3, lineHeight: 1.1, whiteSpace: "nowrap", flexShrink: 0 }}>
      {dot && <span aria-hidden style={{ width: 6, height: 6, borderRadius: 3, background: t.c, display: "inline-block" }} />}
      {children}
    </span>
  );
}

/** The design system's thin-stroke icons the Team artboard uses. */
export function DsIcon({ name, size = 14, color = "currentColor", style }: { name: "plus" | "chevron" | "calendar" | "send" | "bolt" | "close" | "arrowRight" | "check" | "person" | "pin" | "list" | "search"; size?: number; color?: string; style?: React.CSSProperties }) {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", style: { display: "block", flexShrink: 0, ...style } } as const;
  switch (name) {
    case "plus":
      return <svg {...common}><path d="M12 5v14M5 12h14" stroke={color} strokeWidth="2.2" strokeLinecap="round" /></svg>;
    case "chevron":
      return <svg {...common}><path d="M9 5l7 7-7 7" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    case "search":
      return <svg {...common}><circle cx="11" cy="11" r="6.5" stroke={color} strokeWidth="2" /><path d="M20 20l-4.2-4.2" stroke={color} strokeWidth="2" strokeLinecap="round" /></svg>;
    case "calendar":
      return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2" stroke={color} strokeWidth="1.5" /><path d="M7 2v4M17 2v4M3 10h18" stroke={color} strokeWidth="1.5" strokeLinecap="round" /></svg>;
    case "send":
      return <svg {...common}><path d="M21 3L10 14M21 3l-7 18-4-7-7-4 18-7z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" /></svg>;
    case "bolt":
      return <svg {...common}><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill={color} /></svg>;
    case "close":
      return <svg {...common}><path d="M5 5l14 14M19 5L5 19" stroke={color} strokeWidth="1.8" strokeLinecap="round" /></svg>;
    case "arrowRight":
      return <svg {...common}><path d="M4 12h15M13 6l6 6-6 6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    case "check":
      return <svg {...common}><path d="M4 12.5l5 5L20 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    case "person":
      return <svg {...common}><circle cx="12" cy="8" r="4" stroke={color} strokeWidth="1.7" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke={color} strokeWidth="1.7" strokeLinecap="round" /></svg>;
    case "pin":
      return <svg {...common}><path d="M12 2C8 2 5 5 5 8.8 5 14 12 22 12 22s7-8 7-13.2C19 5 16 2 12 2z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" /><circle cx="12" cy="9" r="2.6" stroke={color} strokeWidth="1.6" /></svg>;
    case "list":
      return <svg {...common}><path d="M4 6h16M4 12h16M4 18h16" stroke={color} strokeWidth="1.6" strokeLinecap="round" /></svg>;
  }
}

const STATUS_TONE: Record<string, "live" | "warning" | "danger" | "neutral"> = {
  done: "live",
  running: "warning",
  queued: "warning",
  failed: "danger",
  skipped: "danger",
  pending: "neutral",
};

/** The design system's SegmentedTabs: mono uppercase tabs on one hairline,
 *  the active one underlined in ink. */
export function MetricToggle({ value, onChange, trailing, options, swatches, counts }: {
  value: ProcoreMetric;
  onChange: (m: ProcoreMetric) => void;
  trailing?: React.ReactNode;
  /** The tabs to offer; all of them by default. */
  options?: ProcoreMetric[];
  /** A tab's colour, as its series is drawn — the tabs are the chart's legend. */
  swatches?: Partial<Record<ProcoreMetric, string>>;
  /** A tab's pilot total, printed after its name. */
  counts?: Partial<Record<ProcoreMetric, number>>;
}) {
  return (
    <div style={{ display: "flex", alignItems: "stretch" }}>
    <div role="tablist" data-testid="procore-metric" style={{ display: "flex", flex: 1, minWidth: 0, gap: 22, overflowX: "auto", borderBottom: `1px solid ${DS.line}`, scrollbarWidth: "none" }}>
      {PROCORE_METRICS.filter((m) => !options || options.includes(m.id)).map((m) => {
        const on = m.id === value;
        const swatch = swatches?.[m.id];
        const count = counts?.[m.id];
        return (
          <button
            key={m.id}
            className="kb"
            role="tab"
            aria-selected={on}
            data-metric={m.id}
            data-count={count}
            title={m.hint}
            onClick={() => onChange(m.id)}
            style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 7, border: "none", background: "transparent", padding: "0 0 10px", cursor: "pointer", fontFamily: FONT_MONO, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: on ? DS.ink : DS.ink2, borderBottom: on ? `2px solid ${DS.ink}` : "2px solid transparent", marginBottom: -1, transition: "color 140ms", whiteSpace: "nowrap" }}
          >
            {swatch && <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: swatch, display: "block", flexShrink: 0, opacity: on ? 1 : 0.85 }} />}
            {m.label}
            {count !== undefined && <span style={{ fontSize: 9.5, fontWeight: 600, color: on ? DS.ink3 : DS.meta, letterSpacing: "0.04em" }}>{count}</span>}
          </button>
        );
      })}
    </div>
    {trailing && (
      <div style={{ display: "flex", alignItems: "flex-start", flexShrink: 0, padding: "0 0 8px 16px", borderBottom: `1px solid ${DS.line}` }}>
        {trailing}
      </div>
    )}
    </div>
  );
}

/**
 * Daily or weekly points on the card's chart. A two-way pill in the tabs'
 * mono voice, ink when on; `role="radiogroup"` because exactly one is.
 * It changes only the chart — the hero and footer stay weekly averages,
 * which is what the pilot measures.
 */
export function GrainToggle({ value, onChange, testId = "procore-grain" }: { value: ProcoreBaselineGrain; onChange: (g: ProcoreBaselineGrain) => void; testId?: string }) {
  return (
    <div role="radiogroup" aria-label="Chart resolution" data-testid={testId} style={{ display: "inline-flex", padding: 2, borderRadius: 999, border: `1px solid ${DS.line2}`, background: DS.surface }}>
      {(["day", "week"] as const).map((g) => {
        const on = g === value;
        return (
          <button
            key={g}
            className="kb"
            role="radio"
            aria-checked={on}
            data-grain={g}
            title={g === "day" ? "One point per calendar day" : "One point per week, aligned to the pilot start"}
            onClick={() => onChange(g)}
            style={{ border: "none", height: 22, padding: "0 10px", borderRadius: 999, cursor: "pointer", fontFamily: FONT_MONO, fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", background: on ? DS.ink : "transparent", color: on ? DS.surface : DS.ink2, transition: "background 140ms, color 140ms", whiteSpace: "nowrap" }}
          >
            {g === "day" ? "Daily" : "Weekly"}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The artboard's filter chips under the tabs: a 32px pill with a checkbox
 * square, the field's glyph and the label; ink when on. `role="switch"`
 * because that is what each one is.
 */
export function FilterChips({ lens, onChange, testId = "procore-filter", lead }: {
  lens: ProcoreLens;
  onChange: (l: ProcoreLens) => void;
  testId?: string;
  /** A filter set elsewhere — the chart's date range — drawn first in the row. */
  lead?: React.ReactNode;
}) {
  const filters = lensFilters(lens.metric);
  if (filters.length === 0 && !lead) return null;
  return (
    <div data-testid={`${testId}s`} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 12 }}>
      {lead}
      {filters.map((f) => {
        const on = lens[f.key];
        return (
          <button
            key={f.key}
            className="kb"
            type="button"
            role="switch"
            aria-checked={on}
            data-testid={testId}
            data-filter={f.key}
            data-on={on ? "1" : "0"}
            onClick={() => onChange({ ...lens, [f.key]: !on })}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 13px 0 10px", borderRadius: 16, background: on ? DS.surface : DS.rowHover, border: `1px solid ${on ? "rgba(10,10,10,.2)" : "rgba(10,10,10,.1)"}`, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap", transition: "transform 120ms" }}
            onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.97)"; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          >
            <span aria-hidden style={{ width: 16, height: 16, borderRadius: 5, border: `1.5px solid ${on ? DS.ink : DS.faint}`, background: on ? DS.ink : DS.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {on && <DsIcon name="check" size={11} color={DS.surface} />}
            </span>
            <DsIcon name={f.icon} size={13} color={DS.link} />
            <span style={{ fontSize: 13, fontWeight: 700, color: DS.ink }}>{f.label}</span>
          </button>
        );
      })}
    </div>
  );
}

const DATE_INPUT: React.CSSProperties = { height: 40, padding: "0 12px", borderRadius: 999, border: `1px solid ${DS.line2}`, fontFamily: "inherit", fontSize: 14, background: DS.surface, color: DS.ink };

/** "Oct 5, 10:09 AM" — the artboard's clock. */
function stampOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${day}, ${time}`;
}
/** `YYYY-MM-DD` + days, in UTC. */
function addDaysYmd(ymd: string, days: number): string {
  return new Date(Date.parse(`${ymd}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}
/** "Aug 10" from a `YYYY-MM-DD`, in UTC so the day never shifts. */
function dayOf(ymd: string): string {
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}


// ---------------------------------------------------------------------------
// The Procore card
// ---------------------------------------------------------------------------

/** The record types the Records chart stacks, bottom to top, in the type
 *  colours used everywhere else (`Led AI - Records vs Photos Chart.dc.html`). */
const RECORD_TYPES: Array<{ id: ProcoreMetric; label: string; color: string }> = [
  { id: "dailyLogs", label: "Daily log entries", color: DS.typeDailyLog },
  { id: "observations", label: "Observations", color: DS.typeObservation },
  { id: "tasks", label: "Tasks", color: DS.typeTask },
  { id: "punchCreated", label: "Punch", color: DS.typePunch },
];
const RECORD_TABS: ProcoreMetric[] = ["authored", "dailyLogs", "observations", "tasks", "punchCreated"];
const TYPE_SWATCHES = Object.fromEntries(RECORD_TYPES.map((t) => [t.id, t.color])) as Partial<Record<ProcoreMetric, string>>;

type Support = { label: string; value: string; unit: string; delta?: string; tone?: "cp"; hint?: string };

/**
 * One chart's words and figures for a lens: the note under its title (the
 * comparison the hero used to carry, now in a sentence with its figure) and
 * the stat row. The rule: only a FINISHED period — a checkpoint, or the
 * ramp-up before CP1 ends — is compared against the baseline; a period still
 * under way shows its pace without a change, and before anything has
 * finished the note leads with the running period's pace.
 */
function panelFigures(index: ProcoreIndex | null, pool: ProcoreBaselineUserRow[], byDay: Record<string, ProcoreBaselineCounts> | undefined, weeks: ProcoreBaselineWeek[], lens: ProcoreLens): { hero: string; note: string; supports: Support[]; unit: string; baseUnit: string } {
  const value: SeriesValue = (c) => lensValue(c, lens);
  const unit = lensUnit(lens);
  const baseUnit = PROCORE_METRICS.find((m) => m.id === lens.metric)!.unit;
  const Unit = `${unit[0]!.toUpperCase()}${unit.slice(1)}`;
  if (!index) return { hero: "—", note: "", supports: [], unit, baseUnit };
  if (!index.hasPilot) {
    const last = weeks[weeks.length - 1];
    const prev = weeks[weeks.length - 2];
    const lastV = last ? value(last.counts) : 0;
    const prevV = prev ? value(prev.counts) : 0;
    const change = percentChange(prevV, lastV);
    return {
      hero: String(lastV),
      note: `${unit} this week${change !== null ? ` · ${fmtDelta(change)} vs last` : ""}`,
      supports: index.frame.windows.map((w) => ({ label: w.label, value: String(pool.reduce((n, u) => n + value(sumInWindow(u.byDay, w)), 0)), unit: baseUnit, hint: w.hint })),
      unit,
      baseUnit,
    };
  }
  const { windows, today } = index.frame;
  const baselineAvg = poolRate(byDay, index.frame, "baseline", value);
  const phase = endedPhase(windows, today);
  const phaseRate = phase ? weeklyRate(byDay, phase, today, value) : null;
  const change = baselineAvg !== null && phaseRate !== null ? percentChange(baselineAvg, phaseRate) : null;
  const running = runningPhase(windows, today);
  const runningRate = running && !phase ? weeklyRate(byDay, running, today, value) : null;
  const Running = running ? `${phaseName(running)[0]!.toUpperCase()}${phaseName(running).slice(1)}` : "";
  // The hero: the change once a period has finished; before that, the running
  // period's pace (a rate, never a change). The note says which and why.
  const hero = change !== null ? fmtDelta(change) : phaseRate !== null && baselineAvg === 0 ? `${fmtAvg(phaseRate)}/wk` : runningRate !== null ? `${fmtAvg(runningRate)}/wk` : "—";
  const note = (() => {
    if (!phase) {
      const pilot = windows.find((w) => w.key === "pilot");
      if (pilot?.start === today) return `${Unit} per week in Procore. The pilot starts today — its pace reads from tomorrow.`;
      if (running && runningRate !== null) return `${Unit} per week in Procore — ${phaseName(running)} so far. Nothing has finished yet; ${phaseName(running)} ends ${dayOf(running.until ?? running.end)}.`;
      if (running) return `${Unit} per week in Procore. Nothing has finished yet — ${phaseName(running)} ends ${dayOf(running.until ?? running.end)}.`;
      return `${Unit} per week in Procore.`;
    }
    const which = phase.key === "ramp" ? "the ramp-up" : `checkpoint ${cpName(phase).replace("CP", "")} (${cpName(phase)})`;
    const tail = running ? (running.start === today ? ` ${Running} starts today.` : ` ${Running} is still running.`) : "";
    if (change === null && phaseRate !== null && baselineAvg === 0) return `${Unit} per week in Procore — ${which}; the baseline had none.${tail}`;
    if (change === null) return `${Unit} per week in Procore — ${which} has no baseline to compare against.`;
    return `${change < 0 ? "Fewer" : "More"} ${unit} per week in Procore — ${which} vs. baseline.${tail}`;
  })();
  // One column per period that has begun: its weekly pace, and the change
  // against the baseline only once it has ended. Future checkpoints have no
  // column; the chart still draws their bands.
  const column = (w: ProcoreBaselineWindow, label: string, tone?: "cp"): Support => {
    const rate = weeklyRate(byDay, w, today, value);
    const done = hasEnded(w, today);
    const d = done && rate !== null && baselineAvg !== null ? percentChange(baselineAvg, rate) : null;
    // The arithmetic, spelled out: a period rarely spans whole chart weeks,
    // so its pace is NOT the average of the bars it covers.
    const through = done ? w.end : addDays(today, -1);
    const days = through >= w.start ? daysBetween(w.start, through) + 1 : 0;
    const total = days > 0 ? Object.entries(byDay ?? {}).reduce((n, [ymd, c]) => (ymd >= w.start && ymd <= through ? n + value(c) : n), 0) : 0;
    const sums = days > 0 && rate !== null ? ` ${total} ${baseUnit} over ${plural(days, "day", "days")} (${dayOf(w.start)} – ${dayOf(through)}) ÷ ${days} × 7 = ${fmtAvg(rate)} a week.` : "";
    return { label: done ? label : `${label} · so far`, value: fmtAvg(rate), unit: rate === null ? "" : "/wk", delta: d !== null ? fmtDelta(d) : undefined, tone, hint: done ? `${w.hint}${sums}` : `${w.hint}${sums} Through yesterday; compared once it ends.` };
  };
  const ramp = rampWindow(windows, today);
  // The week's count, as a tally rather than a pace, never a change. The bins
  // run only to the last sync, so the week containing today is drawn only
  // once a sync has reached it: then it reads "This week · so far"; until
  // then the column shows the latest synced week, named as such.
  const drawnWeeks = weeks.filter((b) => !b.future);
  const current = drawnWeeks.find((b) => b.start <= today && today <= b.end);
  const thisWeek = current ?? drawnWeeks[drawnWeeks.length - 1];
  const weekLabel = !thisWeek ? "" : current ? "This week · so far" : daysBetween(thisWeek.end, today) <= 7 ? "Last week" : `Week of ${dayOf(thisWeek.start)}`;
  const supports: Support[] = [
    { label: "Baseline", value: fmtAvg(baselineAvg), unit: "/wk", hint: "Weekly average, 4 weeks before the pilot." },
    ...(ramp && !ramp.future ? [column(ramp, "Ramp up")] : []),
    ...windows.filter((w) => /^cp\d$/.test(w.key) && !w.future).map((w) => column(w, cpName(w), "cp")),
    ...(thisWeek ? [{ label: weekLabel, value: String(value(thisWeek.counts)), unit: baseUnit, hint: `${Unit} from ${dayOf(thisWeek.start)}${current ? " through the last sync" : ` to ${dayOf(thisWeek.end)}`}, a count rather than a pace.` }] : []),
    { label: "Pilot total", value: String(pool.reduce((n, u) => n + value(sumInWindow(u.byDay, windows.find((w) => w.key === "pilot"))), 0)), unit: baseUnit, hint: `Every ${singular(unit)} since the pilot start.` },
  ];
  return { hero, note, supports, unit, baseUnit };
}

/** Records | Photos, each with its pilot total — the switch between the
 *  card's two series (`Led AI - Records vs Photos Chart.dc.html`, 1b). */
function ViewTabs({ value, onChange, tabs }: { value: ProcoreView; onChange: (v: ProcoreView) => void; tabs: Array<{ id: ProcoreView; label: string; count: number }> }) {
  return (
    <div role="tablist" data-testid="procore-view" style={{ display: "inline-flex", padding: 3, borderRadius: 12, background: DS.paper, border: `1px solid ${DS.line}` }}>
      {tabs.map((t) => {
        const on = t.id === value;
        return (
          <button
            key={t.id}
            className="kb"
            type="button"
            role="tab"
            aria-selected={on}
            data-view={t.id}
            onClick={() => onChange(t.id)}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 14px", borderRadius: 9, border: "none", background: on ? DS.surface : "transparent", boxShadow: on ? "0 1px 2px rgba(10,10,10,.08), 0 0 0 1px rgba(10,10,10,.06)" : "none", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, color: on ? DS.ink : DS.link, cursor: "pointer", whiteSpace: "nowrap", transition: "background 140ms, color 140ms" }}
          >
            {t.label}
            <span style={{ fontFamily: FONT_MONO, fontSize: 10, fontWeight: 600, color: on ? DS.ink3 : DS.meta }}>{t.count}</span>
          </button>
        );
      })}
    </div>
  );
}
type ProcoreView = "records" | "photos";

export function ProcoreCard({
  search,
  response,
  index,
  lens,
  onLens,
  onChanged,
  card,
}: {
  search: string;
  response: ProcoreBaselineResponse;
  index: ProcoreIndex | null;
  lens: ProcoreLens;
  onLens: (l: ProcoreLens) => void;
  /** Re-read the stored state — after a pull starts or the setup saves. */
  onChanged: () => void;
  /** The Team page's card shell, so this card is drawn like its neighbours. */
  card: React.CSSProperties;
}) {
  const { pull, setup, linkedProjects } = response;
  const active = pull?.status === "queued" || pull?.status === "running";
  const [flow, setFlow] = useState<null | "setup" | "schedule" | "people" | "retry">(null);
  /** The chart's grain, for whichever tab is open. The figures beside and below it are weekly regardless. */
  const [grain, setGrain] = useState<ProcoreBaselineGrain>("week");

  // Before the first read: an empty chart and one prompt.
  const unset = !pull && !active;

  const pool = index?.pool ?? [];
  const byDay = useMemo(() => sumByDay(pool), [pool]);
  const weeks = useMemo(() => (index ? weeklySeries(index.frame, byDay) : []), [index, byDay]);
  const chosen = !!setup?.cohortChosen && (setup?.cohort.length ?? 0) > 0;
  const chartable = pool.some((u) => u.byDay);
  const stamp = pull ? whenStamp(pull.finishedAt ?? pull.startedAt ?? pull.requestedAt) : null;
  const nextRead = !active && pull?.status === "done" ? nextWeeklyReadAt(setup, pull, CLOCK.now()) : null;
  const readsOver = !!setup?.pilotStartDate && !active && !!pull && nextRead === null;
  const projectWord = plural(linkedProjects.length, "Procore project", "Procore projects");

  // Two series under Records | Photos tabs (`Led AI - Records vs Photos
  // Chart.dc.html`, 1b), one chart at a time: Records, stacked by type with
  // the type tabs and their filters beneath; Photos, a single series with
  // its own filters. The page's lens is the Records one — the roster's cells
  // read it — and the Photos lens lives here.
  const recordsLens: ProcoreLens = lens.metric === "photos" ? { ...lens, metric: "authored" } : lens;
  const [photosLens, setPhotosLens] = useState<ProcoreLens>({ ...DEFAULT_LENS, metric: "photos" });
  /** Which series the card shows: Records | Photos tabs, one chart at a time (1b). */
  const [view, setView] = useState<ProcoreView>("records");
  const pilotWindow = index?.frame.windows.find((w) => w.key === "pilot");
  const pilotTotal = (fn: SeriesValue) => pool.reduce((n, u) => n + fn(sumInWindow(u.byDay, pilotWindow)), 0);
  const panel = (lensOf: ProcoreLens, grain: ProcoreBaselineGrain) => {
    const value: SeriesValue = (c) => lensValue(c, lensOf);
    const series = index && grain === "day" ? seriesAt("day", index.frame, byDay) : weeks;
    return { value, series, ...panelFigures(index, pool, byDay, weeks, lensOf) };
  };
  const records = panel(recordsLens, grain);
  const photos = panel(photosLens, grain);
  const open = view === "records" ? records : photos;
  const stack = recordsLens.metric === "authored" ? RECORD_TYPES.map((t) => ({ key: t.id, label: t.label, color: t.color, value: (c: ProcoreBaselineCounts) => metricValue(c, t.id) })) : undefined;
  const recordsColor = RECORD_TYPES.find((t) => t.id === recordsLens.metric)?.color ?? DS.ink;
  // The type tabs are the chart's legend: each in its colour, with its pilot
  // total (of the plain series, whatever the filters). So are the view tabs.
  const typeCounts = Object.fromEntries(RECORD_TABS.map((id) => [id, pilotTotal((c) => metricValue(c, id))])) as Partial<Record<ProcoreMetric, number>>;
  const recordsTotal = typeCounts.authored ?? 0;
  const photosTotal = pilotTotal((c) => metricValue(c, "photos"));

  // A drag across the chart selects a run of bins and makes it a date filter:
  // the chart zooms to that range, a chip in the filter row names it (its ×
  // clears it), and the hero, note and a "Selected" column read it.
  const [range, setRange] = useState<{ start: string; end: string } | null>(null);
  const inRange = (ymd: string) => !!range && ymd >= range.start && ymd <= range.end;
  const selected = range
    ? (() => {
        const total = pool.reduce((n, u) => n + Object.entries(u.byDay ?? {}).reduce((m, [ymd, c]) => (inRange(ymd) ? m + open.value(c) : m), 0), 0);
        const days = daysBetween(range.start, range.end) + 1;
        const span = days % 7 === 0 ? plural(days / 7, "week", "weeks") : plural(days, "day", "days");
        return { total, days, span, rate: (total / days) * 7 };
      })()
    : null;
  const heroShown = selected ? String(selected.total) : open.hero;
  const Unit = `${open.unit[0]!.toUpperCase()}${open.unit.slice(1)}`;
  const noteShown = selected ? `${Unit} from ${dayOf(range!.start)} to ${dayOf(range!.end)} — ${selected.span} selected on the chart, ${fmtAvg(selected.rate)} a week.` : open.note;
  const rangeChip = range ? (
    <span data-testid="procore-range" style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 6px 0 11px", borderRadius: 16, background: DS.surface, border: "1px solid rgba(10,10,10,.2)", whiteSpace: "nowrap" }}>
      <DsIcon name="calendar" size={13} color={DS.link} />
      <span style={{ fontSize: 13, fontWeight: 700, color: DS.ink }}>{dayOf(range.start)} – {dayOf(range.end)}</span>
      <button className="kb" type="button" aria-label="Clear the date range" data-testid="procore-range-clear" onClick={() => setRange(null)} style={{ width: 20, height: 20, borderRadius: 10, border: "none", background: DS.paper, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
        <DsIcon name="close" size={10} color={DS.ink} />
      </button>
    </span>
  ) : null;
  const supportsShown: Support[] = selected ? [{ label: "Selected", value: String(selected.total), unit: open.baseUnit, hint: `${Unit} from ${dayOf(range!.start)} to ${dayOf(range!.end)}.` }, ...open.supports] : open.supports;

  return (
    <div data-testid="procore-card" style={{ ...card, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "20px 22px 0" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.4px", color: DS.ink }}>{view === "records" ? "Records" : "Photos"} in Procore</span>
            <DsPill tone="neutral">Led AI internal</DsPill>
            {pull && pull.status !== "done" && <DsPill tone={STATUS_TONE[pull.status]}>{pull.status}</DsPill>}
          </div>
          <div data-testid="procore-meta" style={{ ...LABEL, fontSize: 10, fontWeight: 500, letterSpacing: "1px", marginTop: 7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {unset
              ? "What the pilot's people record in Procore, before and during the pilot"
              : [
                  setup?.pilotStartDate ? `Pilot since ${dayOf(setup.pilotStartDate)}` : null,
                  chosen ? plural(setup!.cohort.length, "person", "people") : null,
                  stamp ? `Synced ${stampOf(pull!.finishedAt ?? pull!.startedAt ?? pull!.requestedAt)}` : null,
                  nextRead ? `Next ${nextRead.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : readsOver ? "Weekly syncs finished" : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
          </div>
        </div>
        {!unset && !active && (
          <span style={{ display: "inline-flex", gap: 18 }}>
            <button className="kb teamlink" data-testid="procore-edit-schedule" onClick={() => setFlow("schedule")} style={{ border: "none", background: "transparent", fontFamily: "inherit", fontSize: 14, fontWeight: 700, color: DS.link, cursor: "pointer", whiteSpace: "nowrap", padding: "4px 0" }}>
              Edit schedule
            </button>
            {chosen && (
              <button className="kb teamlink" data-testid="procore-edit-people" onClick={() => setFlow("people")} style={{ border: "none", background: "transparent", fontFamily: "inherit", fontSize: 14, fontWeight: 700, color: DS.link, cursor: "pointer", whiteSpace: "nowrap", padding: "4px 0" }}>
                Edit people
              </button>
            )}
          </span>
        )}
        {!unset && !active && !chosen && pull?.status === "done" && (
          <DsButton testId="procore-choose-people" onClick={() => setFlow("people")}>
            Choose the pilot's people
          </DsButton>
        )}
        {/* A read that failed needs a way back in: straight to the read step,
            with the saved date and schedule. */}
        {!active && pull?.status === "failed" && (
          <DsButton testId="procore-retry" onClick={() => setFlow("retry")}>
            Read Procore again
          </DsButton>
        )}
      </div>

      {pull?.error && (
        <div data-testid="procore-problem" role="alert" style={{ margin: "14px 22px 0", padding: "12px 16px", borderRadius: 12, background: DS.redSoft, fontSize: 14, color: DS.ink }}>
          {pull.error}
        </div>
      )}

      {/* Progress while the read runs. */}
      {active && pull && (
        <div data-testid="procore-progress" style={{ margin: "16px 22px 0", display: "flex", alignItems: "center", gap: 14 }}>
          <div role="progressbar" aria-valuenow={pull.progress.projectsDone} aria-valuemax={pull.progress.projectsTotal} style={{ flex: 1, height: 6, borderRadius: 3, background: DS.bandBase, overflow: "hidden" }}>
            <div style={{ width: `${pull.progress.projectsTotal ? Math.max(3, (100 * pull.progress.projectsDone) / pull.progress.projectsTotal) : 3}%`, height: "100%", background: DS.gold, transition: "width 300ms ease" }} />
          </div>
          <span style={{ ...MUTED, whiteSpace: "nowrap" }}>
            {pull.pausedUntil
              ? `Paused until ${new Date(pull.pausedUntil).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} for Procore's rate limit`
              : "Reading Procore"}{" "}
            · {pull.progress.projectsDone}/{pull.progress.projectsTotal} projects · {pull.progress.apiCalls} calls
          </span>
        </div>
      )}

      {/* Empty: the chart's frame, and the one thing to do. */}
      {unset && (
        <div data-testid="procore-empty" style={{ margin: "20px 22px 22px", position: "relative", height: 210, borderRadius: 12, border: `1px dashed ${DS.line2}`, background: "repeating-linear-gradient(to right, transparent 0 calc(100%/12 - 1px), rgba(10,10,10,.035) calc(100%/12 - 1px) calc(100%/12))", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <div style={{ ...MUTED, color: DS.ink2 }}>
              {linkedProjects.length === 0 ? "No Procore-linked projects in this workspace yet." : `${projectWord} linked. Set up the pilot to see the trend.`}
            </div>
            <DsButton variant="accent" size="md" testId="procore-setup" disabled={linkedProjects.length === 0} onClick={() => setFlow("setup")}>
              Set up
            </DsButton>
          </div>
        </div>
      )}

      {/* Records | Photos, with the grain beside; then the hero, note and legend
          beside the open series' chart; its tabs or filters beneath. */}
      {index && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px 24px", padding: "18px 22px 0" }}>
            <ViewTabs value={view} onChange={setView} tabs={[{ id: "records", label: "Records", count: recordsTotal }, { id: "photos", label: "Photos", count: photosTotal }]} />
            {chartable && <GrainToggle value={grain} onChange={setGrain} />}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(200px,260px) minmax(0,1fr)", gap: "16px 32px", padding: "16px 22px 0" }}>
            <div style={{ minWidth: 0 }}>
              <div data-testid="procore-hero" style={{ fontSize: 52, fontWeight: 800, letterSpacing: "-2px", lineHeight: 1, color: DS.ink }}>
                {heroShown}
              </div>
              <div data-testid="procore-note" style={{ ...MUTED, marginTop: 8, textWrap: "pretty" }}>{noteShown}</div>
              {!range && chartable && <div style={{ ...MUTED, fontSize: 12, marginTop: 14, color: DS.meta }}>Drag across the chart to focus on a stretch of it.</div>}
            </div>
            <div style={{ minWidth: 0 }}>
              {chartable ? (
                view === "records" ? (
                  <Trend weeks={records.series} grain={grain} value={records.value} hasPilot={index.hasPilot} windows={index.frame.windows} unit={records.unit} height={230} testId="procore-trend" stack={stack} barColor={recordsColor} seriesLabel={PROCORE_METRICS.find((m) => m.id === recordsLens.metric)!.label} focus={range} onSelect={setRange} />
                ) : (
                  <Trend weeks={photos.series} grain={grain} value={photos.value} hasPilot={index.hasPilot} windows={index.frame.windows} unit={photos.unit} height={230} testId="procore-trend" barColor={DS.photosBar} seriesLabel="Photos" focus={range} onSelect={setRange} />
                )
              ) : (
                <div style={{ ...MUTED, padding: "80px 0", textAlign: "center" }}>The trend appears after the next weekly sync — this one predates per-day counts.</div>
              )}
            </div>
          </div>
          <div style={{ padding: "12px 22px 18px", maxWidth: 680 }}>
            {view === "records" ? (
              <>
                <MetricToggle value={recordsLens.metric} onChange={(metric) => onLens({ ...recordsLens, metric })} options={RECORD_TABS} swatches={TYPE_SWATCHES} counts={typeCounts} />
                <FilterChips lens={recordsLens} onChange={onLens} lead={rangeChip} />
              </>
            ) : (
              <FilterChips lens={photosLens} onChange={setPhotosLens} testId="procore-photos-filter" lead={rangeChip} />
            )}
          </div>
        </>
      )}

      {/* Footer strip: the open series' figures, and the projects read. */}
      {index && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "14px 24px", padding: "16px 22px 18px", borderTop: `1px solid ${DS.line}`, background: DS.rowHover }}>
          <div data-testid="procore-supports" style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap", gap: "10px 0" }}>
            {supportsShown.map((sv, i) => (
              <div key={sv.label} title={sv.hint} style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 5, padding: `2px 22px 2px ${i === 0 ? 0 : 22}px`, borderLeft: i === 0 ? "none" : "1px solid rgba(10,10,10,.08)" }}>
                <span style={{ ...LABEL, letterSpacing: "1.3px", color: sv.tone === "cp" ? DS.bandCpInk : DS.meta }}>{sv.label}</span>
                <span style={{ display: "flex", alignItems: "baseline", gap: 4, whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.6px", fontVariantNumeric: "tabular-nums", color: DS.ink }}>{sv.value}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: DS.ink3 }}>{sv.unit}</span>
                  {sv.delta && <span style={{ fontSize: 12, fontWeight: 700, color: sv.delta.startsWith("-") ? DS.red : DS.green, marginLeft: 3 }}>{sv.delta}</span>}
                </span>
              </div>
            ))}
          </div>
          <div data-testid="procore-projects" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {index.pull.projects.map((p) => {
              const flagged = Object.entries(p.families).filter(([, f]) => f.status !== "ok");
              const tone: "live" | "warning" | "danger" = p.status === "done" ? (flagged.length ? "warning" : "live") : p.status === "skipped" || p.status === "failed" ? "danger" : "warning";
              const title = [
                `${p.env} · company ${p.companyId} · project ${p.projectId}`,
                p.viaEmail ? `via ${p.viaEmail}` : null,
                `${p.apiCalls} calls · ${p.directorySize} in directory`,
                ...flagged.map(([family, f]) => `${family}: ${f.status}${f.status === "truncated" ? ` after ${f.rows} rows (a floor, not a total)` : f.error ? ` — ${f.error}` : ""}`),
                p.error,
              ]
                .filter(Boolean)
                .join("\n");
              return (
                <span key={p.key} data-testid="procore-project" data-status={p.status} title={title} style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 34, padding: "0 6px 0 12px", borderRadius: 17, background: DS.surface, border: "1px solid rgba(10,10,10,.1)", cursor: "default", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: DS.ink }}>{p.projectName}</span>
                  <DsPill tone={tone}>{p.status === "done" ? (flagged.length ? `${flagged.length} flag${flagged.length > 1 ? "s" : ""}` : "Read") : p.status}</DsPill>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {flow && (
        <SetupSheet
          search={search}
          response={response}
          startAt={flow === "people" ? 3 : flow === "retry" ? 2 : 1}
          scheduleOnly={flow === "schedule"}
          onClose={() => setFlow(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The setup sheet — three steps, once
// ---------------------------------------------------------------------------

const PROJECT_WORD = (n: number) => plural(n, "Procore project", "Procore projects");
/** Local aliases so the setup sheet below reads as before. */
const PillButton = DsButton;
/** The picker's search field: a 38px box with the glass, as the artboard draws it. */
function SearchBox({ value, onChange, placeholder, testId }: { value: string; onChange: (v: string) => void; placeholder: string; testId: string }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8, height: 38, marginTop: 8, padding: "0 11px", borderRadius: 10, border: `1px solid ${DS.line2}`, background: DS.surface }}>
      <DsIcon name="search" size={14} color={DS.meta} />
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete="off" style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontFamily: "inherit", fontSize: 14, fontWeight: 500, color: DS.ink }} />
    </span>
  );
}

function Tag({ label, tone, title }: { label: string; tone?: "good" | "warn" | "bad" | "quiet" | "gold"; title?: string }) {
  const map = { good: "live", warn: "warning", bad: "danger", quiet: "neutral", gold: "gold" } as const;
  return <DsPill tone={map[tone ?? "quiet"]} title={title}>{label}</DsPill>;
}

/** The schedule fields the editor shows in weeks rather than days. */
const WEEK_FIELDS = new Set<keyof ProcoreBaselineSchedule>(["baselineDays", "rampDays", "checkpointDays"]);

function SetupSheet({ search, response, startAt, scheduleOnly = false, onClose, onChanged }: { search: string; response: ProcoreBaselineResponse; startAt: 1 | 2 | 3; scheduleOnly?: boolean; onClose: () => void; onChanged: () => void }) {
  const { pull, setup, linkedProjects } = response;
  const active = pull?.status === "queued" || pull?.status === "running";
  const [step, setStep] = useState<1 | 2 | 3>(startAt);
  const [date, setDate] = useState(setup?.pilotStartDate ?? "");
  const [schedule, setSchedule] = useState<ProcoreBaselineSchedule>({ ...(setup?.schedule ?? DEFAULT_SCHEDULE), baselineDays: DEFAULT_SCHEDULE.baselineDays });
  /** The days the ramp and checkpoints need; the pilot can never be shorter. */
  // The schedule on the calendar: whole Monday–Sunday weeks from the chosen
  // start, so the ramp-up and every checkpoint land where the pilot's own
  // windows will (`pilotCalendar`). Without a valid date, the nominal lengths.
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const cal = validDate ? pilotCalendar(date, { ...schedule, baselineDays: DEFAULT_SCHEDULE.baselineDays }) : null;
  const off = (d: string) => daysBetween(date, d);
  const scheduledDays = cal ? off(cal.checkpoints[cal.checkpoints.length - 1]!.end) + 1 : schedule.rampDays + schedule.checkpointDays * schedule.checkpointCount;
  const pilotDays = Math.max(schedule.pilotDays, scheduledDays);
  const isDefaultSchedule = (Object.keys(DEFAULT_SCHEDULE) as Array<keyof ProcoreBaselineSchedule>).every((k) => (k === "pilotDays" ? pilotDays : schedule[k]) === DEFAULT_SCHEDULE[k]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [dateProblem, setDateProblem] = useState<string | null>(null);
  const dateRef = { current: null as HTMLInputElement | null };
  // Validated on Next, from the input itself: a browser's own date picker
  // (and autofill) can set the value without a React change event. Saving
  // the date and schedule HERE (not with the read) means a schedule edit on
  // a workspace that is already set up never touches Procore.
  const next = async () => {
    const v = (dateRef.current?.value ?? date).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      setDateProblem("Pick the day the pilot started.");
      return;
    }
    const chosenSchedule: ProcoreBaselineSchedule = { ...schedule, baselineDays: DEFAULT_SCHEDULE.baselineDays, pilotDays };
    const bad = scheduleProblem(chosenSchedule);
    if (bad) {
      setDateProblem(bad);
      return;
    }
    const asDefault = (Object.keys(DEFAULT_SCHEDULE) as Array<keyof ProcoreBaselineSchedule>).every((k) => chosenSchedule[k] === DEFAULT_SCHEDULE[k]);
    setSchedule(chosenSchedule);
    setDate(v);
    setDateProblem(null);
    setBusy(true);
    const outcome = await saveProcoreBaselineSetup(search, { pilotStartDate: v, schedule: asDefault ? null : chosenSchedule });
    setBusy(false);
    if (!outcome.ok) {
      setDateProblem(outcome.error ?? `Could not save (${outcome.status}).`);
      return;
    }
    onChanged();
    if (scheduleOnly) onClose();
    else setStep(2);
  };
  // The schedule, laid out as the modal draws it: baseline, ramp, each
  // checkpoint, and whatever pilot days follow the last one. Day 0 is the
  // pilot start; the baseline is negative days.
  const todayYmd = CLOCK.now().toISOString().slice(0, 10);
  type Seg = { key: string; name: string; short: string; a: number; b: number; kind: "base" | "ramp" | "cp" | "tail" };
  const segments: Seg[] = (() => {
    if (cal) {
      const out: Seg[] = [{ key: "baseline", name: "Baseline", short: "BASELINE", a: off(cal.baseline.start), b: off(cal.baseline.end), kind: "base" }];
      if (cal.rampEnd) out.push({ key: "ramp", name: "Ramp-up", short: "RAMP", a: 0, b: off(cal.rampEnd), kind: "ramp" });
      cal.checkpoints.forEach((c, i) => out.push({ key: `cp${i + 1}`, name: `Checkpoint ${i + 1}`, short: `CP${i + 1}`, a: off(c.start), b: off(c.end), kind: "cp" }));
      if (pilotDays > scheduledDays) out.push({ key: "tail", name: "After last checkpoint", short: `+${pilotDays - scheduledDays}D`, a: scheduledDays, b: pilotDays - 1, kind: "tail" });
      return out;
    }
    const out: Seg[] = [{ key: "baseline", name: "Baseline", short: "BASELINE", a: -schedule.baselineDays, b: -1, kind: "base" }];
    if (schedule.rampDays > 0) out.push({ key: "ramp", name: "Ramp-up", short: "RAMP", a: 0, b: schedule.rampDays - 1, kind: "ramp" });
    for (let i = 0; i < schedule.checkpointCount; i++) {
      const a = schedule.rampDays + i * schedule.checkpointDays;
      out.push({ key: `cp${i + 1}`, name: `Checkpoint ${i + 1}`, short: `CP${i + 1}`, a, b: a + schedule.checkpointDays - 1, kind: "cp" });
    }
    if (pilotDays > scheduledDays) out.push({ key: "tail", name: "After last checkpoint", short: `+${pilotDays - scheduledDays}D`, a: scheduledDays, b: pilotDays - 1, kind: "tail" });
    return out;
  })();
  const dayAt = (offset: number) => (validDate ? dayOf(addDaysYmd(date, offset)) : "");
  const todayOffset = validDate ? Math.round((Date.parse(`${todayYmd}T00:00:00Z`) - Date.parse(`${date}T00:00:00Z`)) / 86_400_000) : null;
  const SEG_STYLE: Record<Seg["kind"], { bg: string; fg: string; dot: string }> = {
    base: { bg: DS.segBase, fg: DS.body2, dot: DS.dashed },
    ramp: { bg: `repeating-linear-gradient(135deg, ${DS.segRampA} 0 6px, ${DS.segRampB} 6px 12px)`, fg: DS.meta, dot: DS.segDot },
    cp: { bg: DS.segCp, fg: DS.bandCpInk, dot: DS.gold },
    tail: { bg: DS.segTail, fg: DS.ink3, dot: DS.segDot },
  };
  const bump = (key: keyof ProcoreBaselineSchedule, delta: number) => {
    const inWeeks = WEEK_FIELDS.has(key);
    const unit = inWeeks ? 7 : 1;
    const lim = SCHEDULE_LIMITS[key];
    const current = key === "pilotDays" ? pilotDays : schedule[key];
    const min = key === "pilotDays" ? Math.max(lim.min, scheduledDays) : lim.min;
    const next = Math.min(lim.max, Math.max(min, current + delta * unit));
    setSchedule({ ...schedule, [key]: next });
  };
  // The baseline is not a stepper: it is always the four weeks before the
  // pilot (`DEFAULT_SCHEDULE.baselineDays`), shown as a fixed line above.
  // The ramp-up stepper moves whole weeks; what it SAYS adds the start
  // week's remaining business days ("1 week 3 days" for a Wednesday start),
  // and zero weeks is no ramp at all.
  const rampWords = (() => {
    const { weeks, days } = validDate ? rampMakeup(date, schedule.rampDays) : { weeks: schedule.rampDays / 7, days: 0 };
    if (weeks === 0 && days > 0) return { shown: String(days), unit: "days" };
    return { shown: String(weeks), unit: `${weeks === 1 ? "week" : "weeks"}${days > 0 ? ` ${days} day${days === 1 ? "" : "s"}` : ""}` };
  })();
  const steppers: Array<{ key: keyof ProcoreBaselineSchedule; label: string; value: number; shown?: string; unit: string; min: number; max: number; span?: string }> = [
    { key: "rampDays", label: "Ramp-up", value: schedule.rampDays / 7, shown: rampWords.shown, unit: rampWords.unit, min: 0, max: SCHEDULE_LIMITS.rampDays.max / 7 },
    { key: "checkpointCount", label: "Checkpoints", value: schedule.checkpointCount, unit: "", min: SCHEDULE_LIMITS.checkpointCount.min, max: SCHEDULE_LIMITS.checkpointCount.max },
    { key: "checkpointDays", label: "Each checkpoint", value: schedule.checkpointDays / 7, unit: schedule.checkpointDays === 7 ? "week" : "weeks", min: SCHEDULE_LIMITS.checkpointDays.min / 7, max: SCHEDULE_LIMITS.checkpointDays.max / 7 },
    { key: "pilotDays", label: "Pilot length", value: pilotDays, unit: "days", min: Math.max(SCHEDULE_LIMITS.pilotDays.min, scheduledDays), max: SCHEDULE_LIMITS.pilotDays.max },
  ];
  const startLabel = validDate
    ? new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
    : "Pick a date";

  // Step 2 waits on the read; the sheet moves on the moment it finishes.
  const [awaiting, setAwaiting] = useState(false);
  useEffect(() => {
    if (awaiting && pull && !active) {
      setAwaiting(false);
      if (pull.status === "done") setStep(3);
      else setProblem(pull.error ?? "The read failed.");
    }
  }, [awaiting, pull, active]);

  // Step 3 — the picker, as `Led AI - Pilot People Picker.dc.html` draws it:
  // a project on the left, that project's WHOLE directory on the right (not
  // only the people who recorded something), and one running list below,
  // grouped by the project each person was picked from. The directory comes
  // from `/api/procore-baseline/directory` a page at a time — filtered and
  // ordered on the backend, as the project search is — so a 5,000-person
  // project costs the browser 50 rows, not 5,000.
  type PickRow = ProcoreDirectoryRow;
  /** The projects there are people to pick from: the ones the read completed. */
  const pickProjects = useMemo(() => (pull?.projects ?? []).filter((p) => p.status === "done"), [pull]);
  const [proj, setProj] = useState<string>(() => pickProjects[0]?.key ?? "");
  useEffect(() => {
    if (!pickProjects.some((p) => p.key === proj) && pickProjects[0]) setProj(pickProjects[0].key);
  }, [pickProjects, proj]);
  const [pq, setPq] = useState("");
  const [tray, setTray] = useState(false);

  // The directory pane: one project's rows, a page at a time.
  type Dir = { key: string; q: string; rows: PickRow[]; nextCursor: string | null; total: number; all: number; truncated: boolean; loading: boolean; error: string | null };
  const EMPTY_DIR: Dir = { key: "", q: "", rows: [], nextCursor: null, total: 0, all: 0, truncated: false, loading: false, error: null };
  const [dir, setDir] = useState<Dir>(EMPTY_DIR);
  /** Each project's row count as its first page reported it — "N people" on
   *  the project list, kept while a search narrows the pane. */
  const [peopleKnown, setPeopleKnown] = useState<Record<string, number>>({});
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filter.trim()), DIRECTORY_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filter]);
  // A page that answers after a newer request went out is dropped: the pane
  // shows the list the reader asked for last, never an older one on top.
  const dirRequest = useRef(0);
  const loadDirectory = useCallback(
    async (key: string, q: string, cursor: string | null) => {
      if (!key) {
        setDir(EMPTY_DIR);
        return;
      }
      const n = ++dirRequest.current;
      setDir((d) => (cursor ? { ...d, loading: true, error: null } : { ...EMPTY_DIR, key, q, loading: true }));
      let env: ProcoreDirectoryEnvelope;
      try {
        env = await fetchProcoreBaselineDirectory(search, { project: key, q, cursor, limit: DIRECTORY_PAGE_DEFAULT });
      } catch (error) {
        env = { ok: false, error: error instanceof Error ? error.message : String(error) } as ProcoreDirectoryEnvelope;
      }
      if (n !== dirRequest.current) return;
      if (!env.ok || !env.result) {
        setDir((d) => ({ ...d, loading: false, error: env.error ?? "The directory did not load." }));
        return;
      }
      const page = env.result;
      setPeopleKnown((known) => (known[key] === page.all ? known : { ...known, [key]: page.all }));
      setDir((d) => ({
        key,
        q,
        rows: cursor ? [...d.rows, ...page.rows] : page.rows,
        nextCursor: page.nextCursor,
        total: page.total,
        all: page.all,
        truncated: page.truncated,
        loading: false,
        error: null,
      }));
    },
    [search],
  );
  useEffect(() => {
    if (step === 3) void loadDirectory(proj, debouncedQ, null);
  }, [step, proj, debouncedQ, loadDirectory]);
  const loadMore = () => {
    if (dir.nextCursor && !dir.loading) void loadDirectory(dir.key, dir.q, dir.nextCursor);
  };
  // The sentinel under the last row asks for the next page 320px before the
  // reader reaches it; the pane is the root, since it is what scrolls.
  const paneRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = moreRef.current;
    if (!node || !dir.nextCursor || dir.loading || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { root: paneRef.current, rootMargin: "0px 0px 320px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dir.nextCursor, dir.loading, dir.key, dir.q]);

  /** The running list: procoreUserId → the project they were picked from and
   *  the row as it was when picked, in pick order. Keeping the row means a
   *  person picked on page three stays named in the tray after a search
   *  replaces the page. */
  const rowOfCohort = (m: ProcoreBaselineCohortMember): PickRow => ({ procoreUserId: m.procoreUserId, name: m.name, ...(m.email ? { email: m.email } : {}), ...(m.company ? { company: m.company } : {}), records: 0 });
  const homeOf = (id: number) => pickProjects.find((p) => p.users.some((u) => u.procoreUserId === id))?.key ?? pickProjects[0]?.key ?? "";
  const [sel, setSel] = useState<Map<number, { key: string; row: PickRow }>>(
    () => new Map((setup?.cohortChosen ? setup.cohort : []).map((m) => [m.procoreUserId, { key: m.projectKey ?? homeOf(m.procoreUserId), row: rowOfCohort(m) }])),
  );
  // Before anyone has chosen, every matched seat starts checked — from the
  // pull's user rows (the read always stores one for a seat), so the
  // pre-check does not wait on a directory page.
  useEffect(() => {
    if (setup?.cohortChosen || !pull) return;
    const next = new Map<number, { key: string; row: PickRow }>();
    for (const p of pickProjects) {
      for (const u of p.users) {
        if (!u.ledUserId || next.has(u.procoreUserId)) continue;
        next.set(u.procoreUserId, { key: p.key, row: { procoreUserId: u.procoreUserId, name: u.name, ...(u.email ? { email: u.email } : {}), ...(u.company ? { company: u.company } : {}), ledUserId: u.ledUserId, ...(u.inDirectory ? {} : { left: true }), records: 0 } });
      }
    }
    setSel(next);
  }, [pull, setup?.cohortChosen, pickProjects]);
  const togglePick = (row: PickRow) =>
    setSel((prev) => {
      const next = new Map(prev);
      if (next.has(row.procoreUserId)) next.delete(row.procoreUserId);
      else next.set(row.procoreUserId, { key: proj, row });
      return next;
    });
  const removePick = (id: number) =>
    setSel((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  const current = pickProjects.find((p) => p.key === proj);
  const shownRows = dir.key === proj ? dir.rows : [];
  const allOn = shownRows.length > 0 && shownRows.every((r) => sel.has(r.procoreUserId));
  // Select all / Clear all act on the rows loaded so far — the control says so.
  const selectAll = () =>
    setSel((prev) => {
      const next = new Map(prev);
      for (const r of shownRows) {
        if (allOn) next.delete(r.procoreUserId);
        else if (!next.has(r.procoreUserId)) next.set(r.procoreUserId, { key: proj, row: r });
      }
      return next;
    });
  const pqLower = pq.trim().toLowerCase();
  const shownProjects = pickProjects.filter((p) => !pqLower || p.projectName.toLowerCase().includes(pqLower));
  /** "N people" on a project row: the page's count once one has answered, the read's size until then. */
  const peopleIn = (p: ProcoreBaselineProject) => peopleKnown[p.key] ?? (p.directorySize || p.users.length);
  const pickedRows = [...sel.entries()].map(([id, { key, row }]) => ({ id, key, row }));
  const MAX_STACK = 9;
  const stack = pickedRows.slice().reverse().slice(0, MAX_STACK);
  const groups = pickProjects.map((p) => ({ key: p.key, name: p.projectName, members: pickedRows.filter((x) => x.key === p.key) })).filter((g) => g.members.length > 0);
  // Picks from a project the pull no longer has (unlinked since) still count.
  const orphan = pickedRows.filter((x) => !pickProjects.some((p) => p.key === x.key));
  const reviewGroups = [...groups, ...(orphan.length ? [{ key: "elsewhere", name: "Elsewhere", members: orphan }] : [])];
  const initials = (name: string) =>
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  const avatarOf = (id: number) => DS.avatars[id % DS.avatars.length]!;

  const startRead = async () => {
    setBusy(true);
    setProblem(null);
    const outcome = await startProcoreBaselinePull(search, date || null);
    setBusy(false);
    if (!outcome.ok) {
      setProblem(outcome.error ?? `Refused (${outcome.status}).`);
      return;
    }
    setAwaiting(true);
    onChanged();
  };

  const savePeople = async () => {
    setBusy(true);
    setProblem(null);
    const cohort: ProcoreBaselineCohortMember[] = pickedRows.map(({ id, key, row }) => ({
      procoreUserId: id,
      name: row.name,
      ...(row.email ? { email: row.email } : {}),
      ...(row.company ? { company: row.company } : {}),
      ...(key ? { projectKey: key } : {}),
    }));
    const outcome = await saveProcoreBaselineSetup(search, { cohort });
    setBusy(false);
    if (!outcome.ok) {
      setProblem(outcome.error ?? `Refused (${outcome.status}).`);
      return;
    }
    onChanged();
    onClose();
  };

  // Portaled to <body>: the sheet is a child of the Procore card, whose pane
  // sits in a stacking context BELOW the Ask composer rail, so a fixed overlay
  // rendered in place had its footer buttons under the composer. At the root
  // the z-index means what it says.
  return createPortal(
    <div role="presentation" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,.28)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 /* above the Ask composer rail, level with AssignToModal */ }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="procore-setup-title"
        data-testid="procore-setup-sheet"
        data-step={step}
        onClick={(e) => e.stopPropagation()}
        style={
          step === 3
            ? // The picker is a fixed frame with its own scrolling panes.
              { width: "min(860px, 100%)", height: "min(760px, calc(100vh - 32px))", display: "flex", flexDirection: "column", background: DS.surface, borderRadius: 22, boxShadow: "0 30px 70px rgba(10,10,10,.22), 0 0 0 1px rgba(10,10,10,.05)", overflow: "hidden" }
            : { width: "min(560px, 100%)", maxHeight: "calc(100vh - 32px)", display: "flex", flexDirection: "column", background: DS.surface, borderRadius: 22, boxShadow: "0 30px 70px rgba(10,10,10,.22), 0 0 0 1px rgba(10,10,10,.05)", overflow: "hidden auto" }
        }
      >
        <style>{`.procore-date::-webkit-calendar-picker-indicator{position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer}`}</style>
        <div style={{ padding: "22px 26px 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span style={{ ...LABEL, fontSize: 9.5, letterSpacing: "1.4px" }}>{scheduleOnly ? "Pilot schedule" : "Procore setup"}</span>
          <button className="kb" type="button" aria-label="Close" onClick={onClose} style={{ width: 34, height: 34, borderRadius: 17, border: 0, background: DS.paper, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
            <DsIcon name="close" size={14} color={DS.ink} />
          </button>
        </div>

        {step === 1 && (
          <>
            <div style={{ padding: "0 26px" }}>
              <h2 id="procore-setup-title" style={{ margin: "16px 0 0", fontSize: 26, fontWeight: 800, letterSpacing: "-0.8px", color: DS.ink }}>
                Pilot schedule
              </h2>
              <p style={{ margin: "6px 0 0", fontSize: 14.5, lineHeight: 1.55, color: DS.body2, textWrap: "pretty" }}>
                Led AI compares records per week in Procore against the weeks before the pilot, at each checkpoint.
              </p>
            </div>

            <div style={{ padding: "20px 26px 0" }}>
              <div style={LABEL}>Pilot starts</div>
              {/* The artboard's pill: the native date input lies over the whole
                  pill at zero opacity, and its picker indicator is stretched to
                  cover it (the <style> above), so a click anywhere opens the
                  picker; showPicker() is the belt to that brace. */}
              <label
                data-testid="procore-setup-date-pill"
                onClick={() => { const el = dateRef.current as (HTMLInputElement & { showPicker?: () => void }) | null; try { el?.showPicker?.(); } catch { /* already open, or unsupported — the native click still works */ } }}
                style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 10, height: 44, marginTop: 8, padding: "0 16px 0 14px", borderRadius: 12, border: `1px solid ${dateProblem && !validDate ? DS.red : DS.line2}`, background: DS.surface, cursor: "pointer" }}
              >
                <DsIcon name="calendar" size={16} color={DS.ink} />
                <span data-testid="procore-setup-date-label" style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.2px", whiteSpace: "nowrap", color: validDate ? DS.ink : DS.ink3 }}>{startLabel}</span>
                <DsIcon name="chevron" size={11} color={DS.meta} style={{ transform: "rotate(90deg)" }} />
                <input
                  ref={(el) => { dateRef.current = el; }}
                  className="procore-date"
                  data-testid="procore-setup-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-label="Pilot start date"
                  aria-invalid={!!dateProblem && !validDate}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", border: "none", padding: 0 }}
                />
              </label>
              <div data-testid="procore-schedule-baseline" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 13, lineHeight: 1.4, color: DS.body2 }}>
                <span style={{ flex: "none", width: 10, height: 10, borderRadius: 3, background: DS.dashed, display: "block" }} />
                <span>
                  Compared against a <strong style={{ color: DS.ink, fontWeight: 700 }}>4-week baseline</strong> before the start date{cal ? ` (${dayOf(cal.baseline.start)} – ${dayOf(cal.baseline.end)})` : ""}.
                </span>
              </div>
            </div>

            <div data-testid="procore-schedule" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, padding: "18px 26px 0" }}>
              {steppers.map((st) => {
                const atMin = st.value <= st.min;
                const atMax = st.value >= st.max;
                const round: React.CSSProperties = { flex: "none", width: 30, height: 30, borderRadius: 15, background: DS.surface, border: "1px solid rgba(10,10,10,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", fontSize: 17, fontWeight: 600, lineHeight: 1, color: DS.ink, padding: 0 };
                return (
                  <div key={st.key} style={{ borderRadius: 14, background: DS.stepperBg, padding: "12px 12px 10px", gridColumn: st.span ?? "auto" }}>
                    <div style={{ ...LABEL, fontSize: 8.5, whiteSpace: "nowrap" }}>{st.label}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginTop: 8 }}>
                      <button className="kb" type="button" aria-label={`Decrease ${st.label}`} data-testid={`procore-schedule-${st.key}-dec`} disabled={atMin} onClick={() => bump(st.key, -1)} style={{ ...round, cursor: atMin ? "default" : "pointer", opacity: atMin ? 0.35 : 1 }}>
                        −
                      </button>
                      <span style={{ display: "flex", alignItems: "baseline", gap: 3, whiteSpace: "nowrap" }}>
                        <span data-testid={`procore-schedule-${st.key}`} style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.5px", fontVariantNumeric: "tabular-nums", color: DS.ink }}>{st.shown ?? st.value}</span>
                        {st.unit && <span style={{ fontSize: 12, fontWeight: 600, color: DS.ink3 }}>{st.unit}</span>}
                      </span>
                      <button className="kb" type="button" aria-label={`Increase ${st.label}`} data-testid={`procore-schedule-${st.key}-inc`} disabled={atMax} onClick={() => bump(st.key, 1)} style={{ ...round, cursor: atMax ? "default" : "pointer", opacity: atMax ? 0.35 : 1 }}>
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 34px rows and 18px above (the artboard draws 38 / 22): with six
                segments the sheet otherwise overruns an 873px viewport by 23px
                and scrolls inside itself. */}
            <div style={{ padding: "18px 26px 4px" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                <span style={LABEL}>Schedule</span>
                <span data-testid="procore-schedule-summary" style={{ fontSize: 13, fontWeight: 600, color: DS.link, whiteSpace: "nowrap" }}>
                  {validDate ? `${dayAt(0)} – ${dayAt(Math.max(pilotDays, scheduledDays) - 1)} · ${Math.max(pilotDays, scheduledDays)}-day pilot` : `${pilotDays}-day pilot`}
                </span>
              </div>
              <div style={{ display: "flex", gap: 3, height: 34, marginTop: 10 }}>
                {segments.map((g) => (
                  <div key={g.key} style={{ flex: String(g.b - g.a + 1), minWidth: 0, borderRadius: 8, background: SEG_STYLE[g.kind].bg, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    <span style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, letterSpacing: "1px", color: SEG_STYLE[g.kind].fg, whiteSpace: "nowrap" }}>{g.short}</span>
                  </div>
                ))}
              </div>
              <div data-testid="procore-schedule-preview" style={{ marginTop: 14, borderTop: `1px solid ${DS.line}` }}>
                {segments.map((g) => {
                  const now = todayOffset !== null && todayOffset >= g.a && todayOffset <= g.b;
                  return (
                    <div key={g.key} data-testid="procore-schedule-row" style={{ display: "flex", alignItems: "center", gap: 10, height: 34, borderBottom: "1px solid rgba(10,10,10,.06)" }}>
                      <span style={{ flex: "none", width: 10, height: 10, borderRadius: 3, background: SEG_STYLE[g.kind].dot, display: "block" }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, letterSpacing: "-0.15px", whiteSpace: "nowrap", color: DS.ink }}>{g.name}</span>
                      {now && <DsPill tone="live" dot>Now</DsPill>}
                      <span style={{ flex: "none", fontSize: 13.5, fontWeight: 500, color: DS.body2, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{validDate ? `${dayAt(g.a)} – ${dayAt(g.b)}` : `${g.b - g.a + 1} days`}</span>
                    </div>
                  );
                })}
              </div>
              {dateProblem && <div data-testid="procore-setup-date-problem" role="alert" style={{ marginTop: 10, fontSize: 13.3, color: DS.red }}>{dateProblem}</div>}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, padding: "18px 26px 22px", marginTop: 14, borderTop: `1px solid ${DS.line}`, background: DS.rowHover }}>
              <button className="kb" type="button" disabled={busy} onClick={onClose} style={{ height: 44, padding: "0 18px", borderRadius: 22, border: "none", background: "transparent", fontFamily: "inherit", fontSize: 15, fontWeight: 700, color: DS.link, cursor: "pointer" }}>
                Cancel
              </button>
              <button className="kb" type="button" data-testid="procore-setup-next" disabled={busy} onClick={next} style={{ height: 44, padding: scheduleOnly ? "0 22px" : "0 18px 0 20px", borderRadius: 22, border: "none", background: DS.ink, color: DS.surface, display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "inherit", fontSize: 15, fontWeight: 700, cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap", opacity: busy ? 0.6 : 1 }}>
                {busy ? "Saving…" : scheduleOnly ? "Save" : "Continue"}
                {!busy && !scheduleOnly && <DsIcon name="arrowRight" size={14} color={DS.surface} />}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <div style={{ padding: "16px 26px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
            <h2 id="procore-setup-title" style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-0.8px", color: DS.ink }}>
              Read this team's Procore
            </h2>
            <div style={MUTED}>
              Led AI will read {PROJECT_WORD(linkedProjects.length)} now — daily logs, observations, punch items and photos from {schedule.baselineDays} days before{" "}
              {date ? dayOf(date) : "the pilot start"} on — and again once a week until the pilot ends
              {date ? ` (${dayOf(pilotReadsEndOn(date, isDefaultSchedule ? null : schedule))})` : ""}. Reads run under the customer's own users'
              connections, one call at a time. Nothing is written to Procore.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {linkedProjects.map((p) => (
                <span key={p.key} style={{ display: "inline-flex", alignItems: "center", height: 28, padding: "0 10px", borderRadius: 999, border: "1px solid rgba(10,10,10,.1)", fontSize: 13.3, fontWeight: 600 }}>
                  {p.projectName}
                </span>
              ))}
            </div>
            {awaiting && pull && (
              <div data-testid="procore-setup-progress" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div role="progressbar" aria-valuenow={pull.progress.projectsDone} aria-valuemax={pull.progress.projectsTotal} style={{ flex: 1, height: 6, borderRadius: 3, background: C.track, overflow: "hidden" }}>
                  <div style={{ width: `${pull.progress.projectsTotal ? Math.max(3, (100 * pull.progress.projectsDone) / pull.progress.projectsTotal) : 3}%`, height: "100%", background: C.gold, transition: "width 300ms ease" }} />
                </div>
                <span style={{ ...MUTED, whiteSpace: "nowrap" }}>
                  {pull.progress.projectsDone}/{pull.progress.projectsTotal} · {pull.progress.apiCalls} calls
                </span>
              </div>
            )}
            {problem && <div role="alert" style={{ padding: "10px 14px", borderRadius: 10, background: C.redSoft, fontSize: 14, color: C.ink }}>{problem}</div>}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <PillButton variant="ghost" disabled={busy || awaiting} onClick={() => setStep(1)}>
                Back
              </PillButton>
              <PillButton variant="primary" testId="procore-setup-read" busy={busy || awaiting} onClick={startRead}>
                {awaiting ? "Reading…" : busy ? "Starting…" : "Read Procore"}
              </PillButton>
            </div>
          </div>
        )}

        {step === 3 && (
          <>
            <style>{`.pp-proj:hover{background:${DS.pickerProjectHover}}.pp-proj[aria-pressed="true"]:hover{background:${DS.surface}}.pp-row:hover{background:${DS.pickerRowHover}}.pp-row[data-picked="1"]:hover{background:${DS.pickerRowOn}}.pp-x:hover{background:${DS.ghostHover}}.pp-scroll{scrollbar-width:none}.pp-scroll::-webkit-scrollbar{display:none}@keyframes ppChipIn{from{opacity:0;transform:scale(.9)}to{opacity:1;transform:none}}`}</style>
            <div style={{ flex: "none", padding: "12px 26px 16px" }}>
              <h2 id="procore-setup-title" style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-0.8px", color: DS.ink }}>
                Who is in the pilot?
              </h2>
              <p style={{ margin: "6px 0 0", fontSize: 14.5, lineHeight: 1.55, color: DS.body2, textWrap: "pretty" }}>
                Pick a project, check people from its directory, then switch projects to add more. Everyone lands in one list below.
              </p>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "240px minmax(0,1fr)", borderTop: `1px solid ${DS.line}` }}>
              {/* 1 · Project */}
              <div style={{ minHeight: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${DS.line}`, background: DS.rowHover }}>
                <div style={{ flex: "none", padding: "14px 14px 8px" }}>
                  <div style={{ ...LABEL, letterSpacing: "1.3px", padding: "0 4px" }}>1 · Project</div>
                  <SearchBox value={pq} onChange={setPq} placeholder="Find a project" testId="procore-setup-project-filter" />
                </div>
                <div className="pp-scroll" data-testid="procore-setup-projects" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "2px 14px 12px" }}>
                  {shownProjects.map((p) => {
                    const on = p.key === proj;
                    const n = pickedRows.filter((x) => x.key === p.key).length;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        className="kb pp-proj"
                        data-testid="procore-setup-project"
                        data-project={p.key}
                        aria-pressed={on}
                        onClick={() => {
                          setProj(p.key);
                          setFilter("");
                        }}
                        style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10, padding: "10px 10px", borderRadius: 10, border: 0, cursor: "pointer", fontFamily: "inherit", background: on ? DS.surface : "transparent", boxShadow: on ? "0 0 0 1px rgba(10,10,10,.12), 0 2px 6px rgba(10,10,10,.05)" : "none", marginBottom: 2 }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 14, fontWeight: on ? 800 : 600, letterSpacing: "-0.15px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: DS.ink }}>{p.projectName}</span>
                          <span style={{ display: "block", ...LABEL, fontWeight: 600, letterSpacing: ".9px", color: DS.ink3, marginTop: 2 }}>
                            {peopleIn(p)} people{p.directoryTruncated ? " · partial" : ""}
                          </span>
                        </span>
                        {n > 0 && (
                          <span data-testid="procore-setup-project-count" style={{ flexShrink: 0, minWidth: 22, height: 20, padding: "0 6px", borderRadius: 10, background: DS.ink, color: DS.surface, fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {n}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {shownProjects.length === 0 && <div style={{ ...MUTED, padding: "16px 10px", textAlign: "center" }}>{pickProjects.length === 0 ? "No project was read." : "No project matches."}</div>}
                </div>
              </div>

              {/* 2 · People in <project> */}
              <div style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                <div style={{ flex: "none", padding: "14px 18px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "0 2px" }}>
                    <span style={{ ...LABEL, letterSpacing: "1.3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>2 · People in {current?.projectName ?? "…"}</span>
                    {shownRows.length > 0 && (
                      <button type="button" className="kb" data-testid="procore-setup-select-all" title={allOn ? "Uncheck everyone loaded so far" : "Check everyone loaded so far"} onClick={selectAll} style={{ border: 0, background: "transparent", padding: 0, fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: DS.link, cursor: "pointer", whiteSpace: "nowrap" }}>
                        {allOn ? "Clear all" : "Select all"}
                      </button>
                    )}
                  </div>
                  <SearchBox value={filter} onChange={setFilter} placeholder="Find a person or company" testId="procore-setup-filter" />
                </div>
                <div ref={paneRef} className="pp-scroll" data-testid="procore-setup-people" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 10px 10px" }}>
                  {shownRows.map((r) => {
                    const pick = sel.get(r.procoreUserId);
                    const on = !!pick;
                    const elsewhere = pick && pick.key !== proj ? pickProjects.find((p) => p.key === pick.key)?.projectName : undefined;
                    return (
                      <label key={r.procoreUserId} className="pp-row" data-testid="procore-setup-person" data-picked={on ? "1" : "0"} style={{ position: "relative", display: "flex", alignItems: "center", gap: 12, padding: "10px 10px", borderRadius: 10, cursor: "pointer", background: on ? DS.pickerRowOn : "transparent" }}>
                        <input type="checkbox" checked={on} onChange={() => togglePick(r)} style={{ position: "absolute", opacity: 0, width: 0, height: 0, margin: 0 }} />
                        <span aria-hidden style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${on ? DS.ink : DS.faint}`, background: on ? DS.ink : DS.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {on && <DsIcon name="check" size={12} color={DS.surface} />}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                            <span style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: DS.ink }}>{r.name}</span>
                            {r.ledUserId && <Tag label="On Led AI" tone="gold" />}
                            {r.left && <Tag label="left" title="No longer in the project directory" />}
                          </span>
                          <span style={{ display: "block", fontSize: 12.5, color: DS.meta, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {[r.company, r.email].filter(Boolean).join(" · ") || `#${r.procoreUserId}`}
                            {elsewhere ? ` · added from ${elsewhere}` : ""}
                          </span>
                        </span>
                        <span style={{ ...LABEL, letterSpacing: "1px", color: DS.ink3, whiteSpace: "nowrap" }}>{r.records} records</span>
                      </label>
                    );
                  })}
                  {shownRows.length === 0 && !dir.loading && !dir.error && (
                    <div style={{ padding: "28px 10px", textAlign: "center", fontSize: 14, color: DS.meta }}>{dir.q ? `No one in ${current?.projectName ?? "this project"} matches.` : "The read found nobody here."}</div>
                  )}
                  {/* The end of the list: how much is drawn, the next page's trigger, and the sentinel the observer watches. */}
                  <div data-testid="procore-setup-dir-foot" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 10px 2px", minHeight: 34 }}>
                    <span role={dir.error ? "alert" : undefined} style={{ ...LABEL, fontWeight: 600, letterSpacing: ".9px", color: dir.error ? DS.red : DS.ink3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {dir.error
                        ? dir.error
                        : dir.loading && shownRows.length === 0
                          ? "Loading…"
                          : dir.key === proj && dir.total > 0
                            ? `${shownRows.length} of ${dir.total}${dir.truncated ? " · directory cut off at 5,000 — search for anyone missing" : ""}`
                            : ""}
                    </span>
                    {dir.key === proj && dir.nextCursor && (
                      <button type="button" className="kb" data-testid="procore-setup-more" disabled={dir.loading} onClick={loadMore} style={{ border: 0, background: "transparent", padding: 0, fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: DS.link, cursor: dir.loading ? "progress" : "pointer", whiteSpace: "nowrap" }}>
                        {dir.loading ? "Loading…" : "Show more"}
                      </button>
                    )}
                    {dir.error && (
                      <button type="button" className="kb" data-testid="procore-setup-dir-retry" onClick={() => void loadDirectory(proj, debouncedQ, null)} style={{ border: 0, background: "transparent", padding: 0, fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: DS.link, cursor: "pointer", whiteSpace: "nowrap" }}>
                        Try again
                      </button>
                    )}
                  </div>
                  <div ref={moreRef} aria-hidden style={{ height: 1 }} />
                </div>
              </div>
            </div>

            {/* The running list */}
            <div data-testid="procore-setup-tray" style={{ flex: "none", borderTop: `1px solid ${DS.line}`, background: DS.rowHover, padding: "14px 26px" }}>
              <div
                role="button"
                tabIndex={0}
                data-testid="procore-setup-tray-toggle"
                aria-expanded={tray && pickedRows.length > 0}
                onClick={() => pickedRows.length > 0 && setTray((t) => !t)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && pickedRows.length > 0) {
                    e.preventDefault();
                    setTray((t) => !t);
                  }
                }}
                style={{ display: "flex", alignItems: "center", gap: 16, cursor: pickedRows.length ? "pointer" : "default" }}
              >
                <span style={{ flexShrink: 0, display: "flex", alignItems: "baseline", gap: 7 }}>
                  <span data-testid="procore-setup-count" style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-1px", lineHeight: 1, fontVariantNumeric: "tabular-nums", color: DS.ink }}>{pickedRows.length}</span>
                  <span style={{ ...LABEL, letterSpacing: "1.3px" }}>{pickedRows.length === 1 ? "person" : "people"}</span>
                </span>
                <span style={{ flexShrink: 0, display: "flex", alignItems: "center", paddingLeft: 6 }}>
                  {stack.map(({ id, row }) => (
                    <span key={id} title={row.name} style={{ width: 30, height: 30, marginLeft: -7, borderRadius: 15, background: avatarOf(id), color: DS.surface, border: `2px solid ${DS.rowHover}`, fontSize: 9.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", animation: "ppChipIn .2s ease-out both" }}>
                      {initials(row.name)}
                    </span>
                  ))}
                  {pickedRows.length > MAX_STACK && (
                    <span style={{ height: 30, minWidth: 30, marginLeft: -7, padding: "0 8px", borderRadius: 15, background: DS.pickerOverflow, border: `2px solid ${DS.rowHover}`, fontSize: 11, fontWeight: 800, color: DS.link, display: "flex", alignItems: "center", justifyContent: "center" }}>+{pickedRows.length - MAX_STACK}</span>
                  )}
                </span>
                <span style={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {reviewGroups.map((g) => (
                    <span key={g.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 10px", borderRadius: 13, background: DS.surface, border: `1px solid ${DS.line2}`, whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: DS.ink }}>{g.name}</span>
                      <span style={{ fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700, color: DS.meta }}>{g.members.length}</span>
                    </span>
                  ))}
                </span>
                {pickedRows.length > 0 && (
                  <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: DS.link }}>
                    {tray ? "Hide" : "Review"}
                    <DsIcon name="chevron" size={11} color={DS.link} style={{ transform: `rotate(${tray ? -90 : 90}deg)`, transition: "transform .25s" }} />
                  </span>
                )}
              </div>
              {pickedRows.length === 0 && <div style={{ fontSize: 13, color: DS.ink3, marginTop: 8 }}>No one yet — check people above.</div>}
              {tray && pickedRows.length > 0 && (
                <div className="pp-scroll" data-testid="procore-setup-review" style={{ maxHeight: 200, overflowY: "auto", marginTop: 12, paddingTop: 10, borderTop: `1px solid ${DS.line}`, display: "flex", flexDirection: "column", gap: 12 }}>
                  {reviewGroups.map((g) => (
                    <div key={g.key}>
                      <div style={{ ...LABEL, fontSize: 8.5 }}>
                        From {g.name} · {g.members.length}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
                        {g.members.map(({ id, row }) => (
                          <span key={id} data-testid="procore-setup-chip" style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 30, padding: "0 5px 0 4px", borderRadius: 15, background: DS.surface, border: `1px solid ${DS.line2}`, whiteSpace: "nowrap" }}>
                            <span style={{ width: 22, height: 22, borderRadius: 11, background: avatarOf(id), color: DS.surface, fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{initials(row.name)}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.1px", color: DS.ink }}>{row.name}</span>
                            <button type="button" className="kb pp-x" aria-label={`Remove ${row.name}`} onClick={() => removePick(id)} style={{ width: 20, height: 20, borderRadius: 10, border: 0, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
                              <DsIcon name="close" size={10} color={DS.meta} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {problem && <div role="alert" style={{ margin: "12px 26px 0", padding: "10px 14px", borderRadius: 10, background: C.redSoft, fontSize: 14, color: C.ink }}>{problem}</div>}
            <div style={{ flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 26px 18px", borderTop: `1px solid ${DS.line}` }}>
              <span data-testid="procore-setup-summary" style={{ fontSize: 13.5, color: DS.body2 }}>
                {plural(pickedRows.length, "person", "people")} from {plural(reviewGroups.length, "project", "projects")}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PillButton variant="ghost" disabled={busy} onClick={onClose}>
                  Cancel
                </PillButton>
                <PillButton variant="primary" testId="procore-setup-save" busy={busy} onClick={savePeople}>
                  {busy ? "Saving…" : `Save ${pickedRows.length}`}
                </PillButton>
              </span>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Roster cells and the rows for chosen people not on Led AI yet
// ---------------------------------------------------------------------------

export const PROCORE_SPARK_W = 104;

/** The two roster cells: a person's Procore trend in a tinted 34px cell, and
 *  the change. A seat outside the pilot gets a quiet note and a dash. */
export function ProcoreCells({ index, userId, lens }: { index: ProcoreIndex; userId: string; lens: ProcoreLens }) {
  const row = index.bySeat.get(userId);
  const inPilot = index.pilotSeats.size === 0 ? !!row : index.pilotSeats.has(userId);
  if (!inPilot) {
    return (
      <>
        <div data-testid="member-procore-trend" title={row ? "In Procore, not in the pilot" : "Not matched to a Procore user"} style={{ position: "relative", height: 34, borderRadius: 8, background: DS.sparkEmpty, overflow: "hidden" }}>
          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_MONO, fontSize: 9, fontWeight: 600, letterSpacing: "1px", color: DS.dashed }}>{row ? "NOT IN PILOT" : "—"}</span>
        </div>
        <span data-testid="member-procore-change" style={{ textAlign: "right", fontSize: 14, fontWeight: 800, color: DS.faint, fontVariantNumeric: "tabular-nums" }}>
          —
        </span>
      </>
    );
  }
  return <TrendCells index={index} row={row} lens={lens} />;
}

function TrendCells({ index, row, lens }: { index: ProcoreIndex; row: ProcoreBaselineUserRow | undefined; lens: ProcoreLens }) {
  const { weeks, before, now, change, value } = personFigures(index, row, lens);
  const total = weeks.reduce((s, w) => s + value(w.counts), 0);
  const has = !!row?.byDay && total > 0;
  const title = `${total} in the span · ${index.hasPilot ? "baseline" : "last week"} ${fmtAvg(before)} → ${fmtAvg(now)} per week`;
  return (
    <>
      <div data-testid="member-procore-trend" title={title} style={{ position: "relative", height: 34, borderRadius: 8, background: has ? DS.bandCp : DS.sparkEmpty, overflow: "hidden" }}>
        {has ? (
          <Sparkline weeks={weeks} value={value} width={PROCORE_SPARK_W} height={34} />
        ) : (
          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_MONO, fontSize: 9, fontWeight: 600, letterSpacing: "1px", color: DS.dashed }}>NO DATA</span>
        )}
      </div>
      <span data-testid="member-procore-change" title={title} style={{ textAlign: "right", fontSize: 14, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: change === null ? DS.faint : change >= 0 ? DS.green : DS.red }}>
        {fmtDelta(change)}
      </span>
    </>
  );
}

/**
 * A chosen person who has no Led AI seat yet, in the roster's own grid: a
 * dashed ring for an avatar, the Led AI columns empty on purpose, and where a
 * member's Procore cells go, the one thing to do — Send invite. The Procore
 * trend itself is in the row's tooltip; the day this person signs in they
 * become an ordinary member row and it moves into the cell.
 */
export function PendingPersonRow({ index, person, lens, grid, recordColumns, invite }: { index: ProcoreIndex; person: CohortPerson; lens: ProcoreLens; grid: string; recordColumns: number; invite?: InviteContext }) {
  const initials = person.member.name
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const email = person.member.email?.toLowerCase();
  const pending = email ? invite?.byEmail.get(email) : undefined;
  const { before, now, value, weeks } = personFigures(index, person.row, lens);
  const total = weeks.reduce((s, w) => s + value(w.counts), 0);
  // With invites off the pill says it; the line is just their company.
  const status = pending ? `Invited ${whenStamp(pending.createdAt).split(" · ")[0]}` : invite ? "not on Led AI yet" : null;
  return (
    <div
      className="teamrow"
      data-testid="member-pending"
      data-procore-user-id={person.member.procoreUserId}
      title={`${total} in Procore across the span · ${index.hasPilot ? "baseline" : "last week"} ${fmtAvg(before)} → ${fmtAvg(now)} per week`}
      style={{ display: "grid", gridTemplateColumns: grid, alignItems: "center", gap: "0 6px", minHeight: 66, padding: "10px 18px", borderBottom: "1px solid rgba(10,10,10,.06)" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <span style={{ flex: "none", width: 36, height: 36, borderRadius: 18, border: `1.5px dashed ${DS.dashed}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: DS.meta }}>{initials}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.2px", color: DS.body2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{person.member.name}</div>
          <div data-testid="member-pending-status" title={pending ? `Invitation sent to ${pending.email}, not accepted yet` : undefined} style={{ fontSize: 12.5, fontWeight: 500, color: DS.meta, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[person.member.company ?? person.member.email, status].filter(Boolean).join(" · ")}
          </div>
        </div>
      </div>
      {/* Walks and the record columns — empty until they onboard. */}
      {Array.from({ length: 1 + recordColumns }, (_, i) => (
        <span key={i} style={{ textAlign: "right", fontSize: i === 0 ? 15 : 14, fontWeight: i === 0 ? 700 : 600, color: DS.faint, fontVariantNumeric: "tabular-nums" }}>
          —
        </span>
      ))}
      <div style={{ minWidth: 0, paddingLeft: 10 }}>
        <span style={{ fontSize: 13.5, fontWeight: 500, color: DS.faint, whiteSpace: "nowrap" }}>—</span>
      </div>
      <div style={{ gridColumn: "span 2", display: "flex", justifyContent: "flex-end" }}>
        {invite ? (
          <InviteAction person={person} pending={pending} invite={invite} />
        ) : (
          <DsPill tone="muted" testId="member-not-joined" title="In the pilot in Procore, but has no Led AI account yet.">Not yet joined</DsPill>
        )}
      </div>
    </div>
  );
}

/**
 * Send invite, or Resend. An invitation goes to the person's Procore email as
 * an ordinary member, through the same console route the Invite page uses,
 * so `backend` applies the same checks. Resend cancels the pending
 * invitation and sends a fresh one — Clerk mails on create, and refuses a
 * duplicate while one is pending, so cancel-then-create is the only way to
 * re-mail.
 */
function InviteAction({ person, pending, invite }: { person: CohortPerson; pending: OrgInvitationRow | undefined; invite: InviteContext }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | "sent" | "resent">(null);
  const [problem, setProblem] = useState<string | null>(null);
  const email = person.member.email;

  if (!email) return <DsPill tone="neutral" title="Procore lists no email for this person, so there is no one to invite.">No email in Procore</DsPill>;
  if (done) return <DsPill tone="live" title={`Invitation sent to ${email}`}>{done === "sent" ? "Invited" : "Resent"}</DsPill>;

  const send = async () => {
    setBusy(true);
    setProblem(null);
    if (pending) {
      const cancel = await deleteMutation(`/api/invitations/${encodeURIComponent(pending.id)}`, invite.search);
      if (!cancel.ok) {
        setBusy(false);
        setProblem(cancel.error ?? `Could not cancel the earlier invitation (${cancel.status}).`);
        return;
      }
    }
    const result = await postMutation("/api/invitations", invite.search, { emails: [email], role: "member" });
    setBusy(false);
    if (!result.ok) {
      setProblem(result.error ?? `The invitation was refused (${result.status}).`);
      invite.onChanged();
      return;
    }
    setDone(pending ? "resent" : "sent");
    invite.onChanged();
  };

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
      <DsButton
        size="sm"
        variant={pending ? "secondary" : "primary"}
        testId={pending ? "pending-resend" : "pending-invite"}
        busy={busy}
        disabled={!invite.canInvite}
        onClick={send}
        leftIcon={<DsIcon name="send" size={13} color={pending ? DS.accentInk : DS.gold} />}
        style={{ height: 34, padding: "0 14px 0 12px" }}
      >
        {busy ? "Sending…" : pending ? "Resend invite" : "Send invite"}
      </DsButton>
      {(problem || (!invite.canInvite && invite.reason)) && (
        <span data-testid="pending-invite-problem" role="alert" style={{ fontSize: 11.5, color: problem ? DS.red : DS.meta, maxWidth: 160, textAlign: "right", lineHeight: 1.3 }}>
          {problem ?? invite.reason}
        </span>
      )}
    </span>
  );
}
